import React, { useState, useEffect, useRef, useCallback } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import * as XLSX from "xlsx";
import { apiUrl } from "../service/apiUrl";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { parse, isValid, format } from "date-fns";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Snackbar,
  InputAdornment,
  MenuItem,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CloudUpload as SubmitIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Home as HomeIcon,
  School as SchoolIcon,
  DirectionsCar as CarIcon,
  MedicalServices as MedicalIcon,
  FamilyRestroom as FamilyIcon,
  Translate as TranslateIcon,
  Refresh as RefreshIcon,
  PhotoCamera as PhotoCameraIcon,
  CameraAlt as CameraAltIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { register } from "../actions/authActions";
import { getMaxServiceNumber, getMaxServiceNumberWithT } from "../service/authService";
import LookupService from "../service/LookupService";
import {
  postEmployeeDetails,
  postEmployeeQualifications,
  postEmployeeDependents,
  postPreviousEmployment,
  postWorkplaceRelationships,
  getAllEmployees,
  getEmployeeByServiceNo,
  getEmployeeDependents,
  getEmployeeQualifications,
  getPreviousEmployment,
  getWorkplaceRelationships,
} from "../actions/EmployeeRegisterAction";
import {
  postPayment,
  postWelfareBenefits,
  postImportantInformation,
  getPayment,
  getWelfareBenefits,
  getImportantInformation,
} from "../actions/EmployeeFinancialAction";

// ─────────────────────────────────────────────────────────────
// DROPDOWN OPTION LISTS
// ─────────────────────────────────────────────────────────────

const educationOptions = [
  "Below Grade 8",
  "Grade 8",
  "Upto GCE O/L",
  "GCE O/L",
  "Upto GCE A/L",
  "GCE A/L",
  "Certificate Course",
  "Diploma",
  "Degree",
  "Masters",
  "Other",
];

const otherQualificationOptions = [
  "Basic Computer",
  "IT",
  "Media",
  "Photography",
  "Video Filming/Editing",
  "Plumbing",
  "Electrical",
  "Painting",
  "Welding",
  "Masonry",
  "Other",
];

const otherSkillOptions = [
  "Music",
  "Dancing",
  "Arts",
  "Announcing",
  "Leadership",
  "English Language",
  "Tamil Language",
  "Hindi Language",
  "Other",
];

const bloodGroupOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const commuteOptions = [
  "Public Transport",
  "Motor Vehicle",
  "Motor Cycle",
  "3 Wheeler",
  "Push Bicycle",
  "By Foot",
  "Other",
];

const illnessOptions = [
  "Diabetes",
  "Blood Pressure",
  "Cardiac Arrest",
  "High Cholesterol",
  "Rheumatic Issues",
  "Other",
];

const familyRelationshipOptions = [
  "Father",
  "Mother",
  "FIL",
  "MIL",
  "Son",
  "Daughter",
];
const collarSizeOptions = [
  "13", "13.5", "14", "14.5", "15", "15.5",
  "16", "16.5", "17", "17.5", "18", "Other",
];

const subDutyTraditionalOptions = [
  "Kariya Karawana",
  "Thewa Kattala",
  "Asgiriya",
  "Malwatta",
  "Not Applicable",
];

const payRollTypeOptions = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
  { value: "V", label: "Voucher" },
];

// ─────────────────────────────────────────────────────────────
// HELPER COMPONENTS
// ─────────────────────────────────────────────────────────────

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: 700,
      color: "#1e0153",
      mb: 2,
      mt: 1,
      borderLeft: "4px solid #1e0153",
      pl: 1.5,
      lineHeight: 1.4,
    }}
  >
    {children}
  </Typography>
);

const SectionDivider = () => (
  <Box sx={{ borderTop: "1px solid #e2e8f0", my: 3 }} />
);

// ─────────────────────────────────────────────────────────────
// REUSABLE DATE PICKER — stores & displays as YYYY/MM/DD
// ─────────────────────────────────────────────────────────────
const DatePickerField = ({ label, value, onChange, name, size = "small", required = false, error, helperText }) => {
  // Parse stored YYYY/MM/DD string → JS Date for the picker
  const parseStored = (v) => {
    if (!v) return null;
    // YYYY/MM/DD
    const m1 = v.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (m1) { const d = new Date(+m1[1], +m1[2] - 1, +m1[3]); return isValid(d) ? d : null; }
    // YYYY-MM-DD
    const m2 = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m2) { const d = new Date(+m2[1], +m2[2] - 1, +m2[3]); return isValid(d) ? d : null; }
    return null;
  };

  const handleChange = (date) => {
    if (!date || !isValid(date)) {
      onChange({ target: { name, value: "" } });
      return;
    }
    const formatted = format(date, "yyyy/MM/dd");
    onChange({ target: { name, value: formatted } });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={label}
        value={parseStored(value)}
        onChange={handleChange}
        format="yyyy/MM/dd"
        slotProps={{
          textField: {
            fullWidth: true,
            size: size,
            required: required,
            error: !!error,
            helperText: helperText || error || "YYYY/MM/DD",
            placeholder: "YYYY/MM/DD",
          },
        }}
      />
    </LocalizationProvider>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const EmployeeRegister = () => {
  const dispatch = useDispatch();

  // ── Redux state for fetched employees ─────────────────────
  // FIX: key must match the store registration: "getAllEmployees"
  const {
    loading: empLoading,
    data: fetchedEmployees,
    error: empError,
  } = useSelector((state) => state.getAllEmployees) || {};

  // ── Tab state ─────────────────────────────────────────────
  const [tabValue, setTabValue] = useState(0);

  // ── Core form state ───────────────────────────────────────
  const [formData, setFormData] = useState({
    ServiceNo: "",
    RecruitmentDate: "",
    ReportName: "",
    FullName: "",
    FirstName: "",
    HomeAddress: "",
    HomeDistance: "",
    MobileNo: "",
    HomeTelephone: "",
    GramaSevakaArea: "",
    DSDivision: "",
    PoliceArea: "",
    ElectoralDivision: "",
    TemporaryAddress: "",
    TemporaryDistance: "",
    HighestEducation: "",
    HighestEducationOther: "",
    OtherQualifications: [],
    OtherQualificationsOther: "",
    OtherSkills: [],
    OtherSkillsOther: "",
    BirthDate: "",
    NIDNo: "",
    MaritalStatus: "Single",
    BloodGroup: "",
    LicenceNo: "",
    OwnsHouse: "No",
    OwnsVehicle: "No",
    VehicleType: "",
    VehicleNumber: "",
    VehicleRegisterOwner: "",
    TransportMethod: "",
    TransportMethodOther: "",
    CollarSize: "",
    CollarSizeOther: "",
    ECName: "",
    ECAddress: "",
    ECMobile: "",
    ECRelationship: "",
    SpouseFullName: "",
    SpouseOccupation: "",
    SpouseBirthDate: "",
    FatherName: "",
    FatherDeceased: false,
    FatherDependentId: null,
    MotherName: "",
    MotherDeceased: false,
    MotherDependentId: null,
    FatherInLawName: "",
    FatherInLawDeceased: false,
    FatherInLawDependentId: null,
    MotherInLawName: "",
    MotherInLawDeceased: false,
    MotherInLawDependentId: null,
    Sickness: [],
    SicknessOther: "",
    SicknessDetails: "",
    NewOld: "Active",
    Remark: "",
    DepartmentCode: "",
    LocationCode: "",
    Roster: "A",
    company: "",
    role: "U",
    SLocation: "",
    Status: "A",
    Qr: "N",
    // New fields from modifications
    DutyType: "",
    ExternalType: "",
    SubDutyTraditional: "",
    PayRollType: "",
    Designation: "",
    WorkLocation: "",
    Division: "",
    HomeTown: "",
    VehicleTypeOwned: "",
    TransportMethod2: [],
    BasisOfEmployment: "",
    AssignmentEndDate: "",
    WorkCategory: "",
    Grade: "",
    ApplicableShifts: "",
    LeavingDate: "",
    LeavingReason: "",
    AttendanceID: "",
  });

  // ── Dynamic list states ───────────────────────────────────
  const [children, setChildren] = useState([]);
  const [prevEmployments, setPrevEmployments] = useState([]);
  const [relatives, setRelatives] = useState([]);
  const [familyIllnesses, setFamilyIllnesses] = useState([]);
  const [monthlyPayments, setMonthlyPayments] = useState([]);
  const [welfareRecords, setWelfareRecords] = useState([]);
  const [importantInfoRecords, setImportantInfoRecords] = useState([]);

  // ── Employee Image ────────────────────────────────────────
  const [imageFile, setImageFile] = useState(null);           // selected File object
  const [imagePreviewUrl, setImagePreviewUrl] = useState(""); // local blob URL (before upload)
  const [savedImageUrl, setSavedImageUrl] = useState("");     // URL from API (after load/upload)
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");
  const [imageLoadAttempt, setImageLoadAttempt] = useState(0);
  // ServiceNos we've already confirmed have no image on the server — once a
  // ServiceNo lands here we never issue another GetNewUserImg call for it.
  const [failedImageIds, setFailedImageIds] = useState(() => new Set());
  // Cache stable timestamps per ServiceNo so the <img src> doesn't change
  // (and therefore doesn't re-fetch) on every unrelated re-render.
  const imageTimestampCache = useRef({});
  // Only the upload flow should trigger retries (the file may still be
  // writing on the backend). A normal missing image should not retry.
  const justUploadedImageRef = useRef(false);

  const IMAGE_UPLOAD_URL = apiUrl("User/NewFileupload");
  const IMAGE_GET_URL    = apiUrl("User/GetNewUserImg");

  // Build full image URL for a given service number. The timestamp is
  // cached per ServiceNo so repeated renders reuse the same URL instead of
  // busting the cache (and re-requesting the image) every time.
  const getEmployeeImageUrl = (sno) => {
    if (!sno) return "";
    if (!imageTimestampCache.current[sno]) {
      imageTimestampCache.current[sno] = Date.now();
    }
    return `${IMAGE_GET_URL}?Sno=${sno}&t=${imageTimestampCache.current[sno]}`;
  };

  // Force a fresh timestamp (used when we know the image actually changed,
  // e.g. right after an upload).
  const refreshEmployeeImageUrl = (sno) => {
    imageTimestampCache.current[sno] = Date.now();
    return getEmployeeImageUrl(sno);
  };

  const markImageFailed = (sno) => {
    if (!sno) return;
    setFailedImageIds((prev) => {
      if (prev.has(sno)) return prev;
      const next = new Set(prev);
      next.add(sno);
      return next;
    });
  };

  // Fetch and set image for the currently edited employee
  const loadEmployeeImage = (sno) => {
    if (!sno) { setSavedImageUrl(""); return; }
    setImageLoadAttempt(0);
    setFailedImageIds((prev) => {
      if (!prev.has(sno)) return prev;
      const next = new Set(prev);
      next.delete(sno);
      return next;
    });
    setSavedImageUrl(refreshEmployeeImageUrl(sno));
  };

  // Called when the <img> tag fails to load. Only retries (with backoff) if
  // we just uploaded a new file, since the backend may take a moment to
  // finish writing it. Otherwise the image genuinely doesn't exist, so we
  // mark it failed once and never call the API for it again.
  const retryEmployeeImage = (sno) => {
    if (!justUploadedImageRef.current) {
      markImageFailed(sno);
      return;
    }
    setImageLoadAttempt((prev) => {
      const nextAttempt = prev + 1;
      const maxAttempts = 3;
      if (nextAttempt <= maxAttempts) {
        const delay = nextAttempt * 800; // 800ms, 1600ms, 2400ms...
        setTimeout(() => {
          setSavedImageUrl(refreshEmployeeImageUrl(sno));
        }, delay);
      } else {
        justUploadedImageRef.current = false;
        markImageFailed(sno);
      }
      return nextAttempt;
    });
  };

  // Handle file input change — show local preview immediately
  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setImageFile(file);
    setImagePreviewUrl(previewUrl);
    setImageUploadError("");
  };

  // Upload image to API
  const handleImageUpload = async () => {
    const sno = formData.ServiceNo;
    if (!imageFile) { setImageUploadError("Please select an image first."); return; }
    if (!sno)       { setImageUploadError("Please enter a Service Number first."); return; }
    setImageUploading(true);
    setImageUploadError("");
    try {
      const fd = new FormData();
      fd.append("ServiceNo", sno);
      fd.append("ImageFile", imageFile);
      const res = await fetch(IMAGE_UPLOAD_URL, { method: "POST", body: fd });
      if (res.ok) {
        // Reload from API — backend may take a moment to finish writing the
        // file, so this uses an initial delay plus retry-on-failure below.
        setSavedImageUrl("");
        justUploadedImageRef.current = true;
        setTimeout(() => loadEmployeeImage(sno), 1200);
        setImageFile(null);
        setImagePreviewUrl("");
      } else {
        setImageUploadError("Upload failed. Please try again.");
      }
    } catch (err) {
      setImageUploadError("Upload failed: " + (err.message || "Network error"));
    } finally {
      setImageUploading(false);
    }
  };

  // ── UI states ─────────────────────────────────────────────
  const [sessionEmployees, setSessionEmployees] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  // const [nextServiceNo, setNextServiceNo] = useState(null);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [workCategoryOptions, setWorkCategoryOptions] = useState([]);
  const designationOptionsRef = useRef([]);
  const locationOptionsRef = useRef([]);
  const divisionOptionsRef = useRef([]);
  const workCategoryOptionsRef = useRef([]);
  const lovDataPromiseRef = useRef(null);
  const [serviceNoType, setServiceNoType] = useState("normal");
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showDirectDialog, setShowDirectDialog] = useState(false);
const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false); 
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });



  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all | Active | Inactive
  const [serviceNoFilter, setServiceNoFilter] = useState("all"); 

// ── Fetch next service number from API and update form ───
  const refreshServiceNumber = async (applyToForm = true, type = serviceNoType) => {
    try {
      const response = type === "T"
        ? await getMaxServiceNumberWithT()
        : await getMaxServiceNumber();

      const row = response?.resultSet?.[0] || {};
      const rawMax = row.maxid ?? row.MaxId ?? row.maxId ?? row.MaxID ?? "";

      let nextServiceNoStr = "";
      const strMax = String(rawMax);

      const prefixMatch = strMax.match(/^([A-Za-z]+)(\d+)$/);
      if (prefixMatch) {
        const prefix = prefixMatch[1].toUpperCase();
        const num = parseInt(prefixMatch[2], 10);
        const padLength = prefixMatch[2].length;
        nextServiceNoStr = `${prefix}${String(num + 1).padStart(padLength, "0")}`;
            } else {
        const maxIdNum = parseInt(strMax, 10);
        if (!isNaN(maxIdNum)) {
          const nextNum = maxIdNum;
          nextServiceNoStr = type === "T"
            ? `T${nextNum}`
            : String(nextNum).padStart(5, "0");
        }
      }


      if (applyToForm) {
        setFormData((prev) => ({ ...prev, ServiceNo: nextServiceNoStr }));
      }
      return nextServiceNoStr;
    } catch (error) {
      console.error("Error fetching max service number:", error);
      if (applyToForm) {
        setFormData((prev) => ({ ...prev, ServiceNo: "" }));
      }
      setSnackbar({
        open: true,
        message: "Could not fetch next service number — please enter it manually",
        severity: "warning",
      });
    }
    return null;
  };


  const loadLovData = useCallback(async () => {
    const normalizeList = (raw, codeKeys, nameKeys) => {
      const list = Array.isArray(raw) ? raw : raw?.resultSet || raw?.data || [];
      return list
        .map((item) => ({
          code: codeKeys.map((k) => item[k]).find((v) => v !== undefined && v !== null) ?? "",
          name: nameKeys.map((k) => item[k]).find((v) => v !== undefined && v !== null) ?? "",
          status: item.Status ?? item.Status ?? "A",
        }))
        .filter((opt) => opt.code !== "" && opt.status !== "I");
    };

    try {
      const data = await LookupService.getDesignations();
      setDesignationOptions(
        normalizeList(data, ["designationCode", "DesignationCode", "desigCode"], ["designationName", "DesignationName", "desigName"])
      );
    } catch (error) {
      console.error("Error loading Designation LOV", error);
    }

    try {
      const data = await LookupService.getLocations();
      setLocationOptions(
        normalizeList(data, ["locCode", "LocationCode", "locationCode", "Code", "code"], ["locName", "LocationName", "locationName", "Name", "name"])
      );
    } catch (error) {
      console.error("Error loading Location LOV", error);
    }

    try {
      const data = await LookupService.getDivisions();
      setDivisionOptions(
        normalizeList(data, ["DivisionCode", "divisionCode", "divCode"], ["DivisionName", "divisionName", "divName"])
      );
    } catch (error) {
      console.error("Error loading Division LOV", error);
    }

    // Work Category comes from the Employee Category lookup (active ones only)
    try {
      const body = await LookupService.getEmployeeCategories();
      const list = Array.isArray(body) ? body : body?.ResultSet || body?.resultSet || [];
      setWorkCategoryOptions(
        list
          .filter((i) => i.HEC_IS_ACTIVE === true || ["A", "1", "TRUE", "Y"].includes(String(i.HEC_IS_ACTIVE ?? "").trim().toUpperCase()))
          .map((i) => ({ code: String(i.HEC_CATEGORY_CODE ?? ""), name: String(i.HEC_CATEGORY_NAME ?? "") }))
          .filter((o) => o.code !== "")
      );
    } catch (error) {
      console.error("Error loading Work Category LOV", error);
    }
  }, []);

  useEffect(() => {
    lovDataPromiseRef.current = loadLovData();
  }, [loadLovData]);

  useEffect(() => { designationOptionsRef.current = designationOptions; }, [designationOptions]);
  useEffect(() => { locationOptionsRef.current = locationOptions; }, [locationOptions]);
  useEffect(() => { divisionOptionsRef.current = divisionOptions; }, [divisionOptions]);
  useEffect(() => { workCategoryOptionsRef.current = workCategoryOptions; }, [workCategoryOptions]);


  const handleServiceNoTypeChange = async (e) => {
    const newType = e.target.value;
    setServiceNoType(newType);
    await refreshServiceNumber(true, newType);
  };

// ── On mount: fetch next service number + all existing employees ──
  useEffect(() => {
    refreshServiceNumber(true);
    dispatch(getAllEmployees());
  }, [dispatch]);

  // ── Normalise an employee record from the API ─────────────
  const normaliseEmployee = (emp) => ({
    id: emp.serviceNo || emp.ServiceNo || emp.id || Math.random(),
    ServiceNo: emp.serviceNo || emp.ServiceNo || "",
    ReportName: emp.reportName || emp.ReportName || emp.name || "",
    MobileNo: emp.mobileNo || emp.MobileNo || "",
    NewOld: (() => {
      const raw = String(emp.newOld ?? emp.NewOld ?? "").trim().toLowerCase();
      return raw === "inactive" || raw === "i" ? "Inactive" : "Active";
    })(),
    statusType: "Saved",
  });

  const apiEmployees = Array.isArray(fetchedEmployees)
    ? fetchedEmployees.map(normaliseEmployee)
    : Array.isArray(fetchedEmployees?.resultSet)
    ? fetchedEmployees.resultSet.map(normaliseEmployee)
    : [];

  // De-duplicate: session employees take priority
  const sessionServiceNos = new Set(sessionEmployees.map((e) => e.ServiceNo));
  const dedupedApiEmployees = apiEmployees.filter(
    (e) => !sessionServiceNos.has(e.ServiceNo)
  );

  // Session employees stay at top (most recently added first — they are prepended on add)
  // API employees sorted by ServiceNo descending (highest = most recent at top)
  // Handles both pure numeric (1011) and prefixed formats (EMP001)
  const parseServiceNo = (sno) => {
    const s = String(sno || "");
    const m = s.match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : 0;
  };
  const sortedApiEmployees = [...dedupedApiEmployees].sort((a, b) =>
    parseServiceNo(b.ServiceNo) - parseServiceNo(a.ServiceNo)
  );

  const allDisplayEmployees = [...sessionEmployees, ...sortedApiEmployees];

  const searchFilteredEmployees = searchTerm.trim()
    ? allDisplayEmployees.filter(
        (emp) =>
          emp.ServiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.ReportName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allDisplayEmployees;

  // Session rows carry the status inside fullData; API rows have NewOld directly
  const getEmployeeActiveStatus = (emp) => emp.fullData?.NewOld || emp.NewOld || "Active";

  const filteredEmployees = searchFilteredEmployees.filter((emp) => {
    if (activeFilter !== "all" && getEmployeeActiveStatus(emp) !== activeFilter) return false;
    if (serviceNoFilter === "all") return true;
    const isT = /^T/i.test(String(emp.ServiceNo || "").trim());
    return serviceNoFilter === "T" ? isT : !isT;
  });

  // ─────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;
    if (name === "ServiceNo") {
      // Normal type: digits only. T type: free text, but no dashes allowed
      filteredValue = serviceNoType === "T" ? value.replace(/-/g, "") : value.replace(/\D/g, "");
    }    setFormData((prev) => ({ ...prev, [name]: filteredValue }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCheckboxToggle = (fieldName, item) => {
    setFormData((prev) => {
      const current = prev[fieldName] || [];
      const updated = current.includes(item)
        ? current.filter((v) => v !== item)
        : [...current, item];
      return { ...prev, [fieldName]: updated };
    });
  };

  // ── Children ──────────────────────────────────────────────
  const handleAddChild = () =>
    setChildren((prev) => [
      ...prev,
      { id: Date.now(), Name: "", BirthDate: "", Sex: "", School: "", Grade: "" },
    ]);

  const handleChildChange = (id, field, value) =>
    setChildren((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );

  const handleRemoveChild = (id) =>
    setChildren((prev) => prev.filter((c) => c.id !== id));

  // ── Previous employment ───────────────────────────────────
  const handleAddPrevJob = () =>
    setPrevEmployments((prev) => [
      ...prev,
      { id: Date.now(), Institute: "", Position: "", From: "", To: "", LeavingReason: "" },
    ]);

  const handlePrevJobChange = (id, field, value) =>
    setPrevEmployments((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );

  const handleRemovePrevJob = (id) =>
    setPrevEmployments((prev) => prev.filter((p) => p.id !== id));

  // ── Relatives ─────────────────────────────────────────────
  const handleAddRelative = () =>
    setRelatives((prev) => [
      ...prev,
      { id: Date.now(), Name: "", ServiceNo: "", Relationship: "" },
    ]);

  const handleRelativeChange = (id, field, value) =>
    setRelatives((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );

  const handleRemoveRelative = (id) =>
    setRelatives((prev) => prev.filter((r) => r.id !== id));

  // ── Family illnesses ──────────────────────────────────────
  const handleAddFamilyIllness = () =>
    setFamilyIllnesses((prev) => [
      ...prev,
      { id: Date.now(), Name: "", Relationship: "", Illness: "" },
    ]);

  const handleFamilyIllnessChange = (id, field, value) =>
    setFamilyIllnesses((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );

  const handleRemoveFamilyIllness = (id) =>
    setFamilyIllnesses((prev) => prev.filter((f) => f.id !== id));

  // ── Validation ────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    const { ServiceNo, ReportName, MobileNo, FullName, NewOld, LeavingDate, LeavingReason } = formData;
    if (NewOld === "Inactive") {
      if (!LeavingDate) newErrors.LeavingDate = "Leaving Date is required for an inactive employee";
      if(!LeavingReason?.trim()) newErrors.LeavingReason = "Leaving Reason is required for an inactive employee";
    }
    if (!ServiceNo?.trim()) newErrors.ServiceNo = "Service Number is required";
      else if (serviceNoType !== "T" && !/^\d+$/.test(ServiceNo)) newErrors.ServiceNo = "Service Number must contain numbers only";
    if (!ReportName?.trim()) newErrors.ReportName = "Name with Initials is required";
    if (!FullName?.trim()) newErrors.FullName = "Full Name (Sinhala) is required";
    // if (!MobileNo?.trim()) newErrors.MobileNo = "Mobile Number is required";
    // else if (MobileNo.length < 10) newErrors.MobileNo = "Valid mobile number required";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const onlyLeavingErrors = Object.keys(newErrors).every(
        (k) => k === "LeavingDate" || k === "LeavingReason"
      );
      setTabValue(onlyLeavingErrors ? 6 : 0);
    }
    return Object.keys(newErrors).length === 0;
  };

  // ── Build the payload for postEmployeeDetails ─────────────
  const buildEmployeePayload = (serviceNo) => ({
    ServiceNo: serviceNo,
    RecruitmentDate: formData.RecruitmentDate,
    ReportName: formData.ReportName,
    FullName: formData.FullName,
    FirstName: formData.FirstName,
    HomeAddress: formData.HomeAddress,
    HomeDistance: parseFloat(formData.HomeDistance) || 0,
    MobileNo: formData.MobileNo,
    HomeTelephone: formData.HomeTelephone,
    GramaSevakaArea: formData.GramaSevakaArea,
    DSDivision: formData.DSDivision,
    PoliceArea: formData.PoliceArea,
    ElectoralDivision: formData.ElectoralDivision,
    TemporaryAddress: formData.TemporaryAddress,
    TemporaryDistance: parseFloat(formData.TemporaryDistance) || 0,
    BirthDate: formData.BirthDate,
    NIDNo: formData.NIDNo,
    MaritalStatus: formData.MaritalStatus,
    BloodGroup: formData.BloodGroup,
    LicenceNo: formData.LicenceNo,
    OwnsHouse: formData.OwnsHouse === "Yes",
    OwnsVehicle: formData.OwnsVehicle === "Yes",
    VehicleType: formData.VehicleType,
    VehicleNumber: formData.VehicleNumber,
    VehicleRegisterOwner: formData.VehicleRegisterOwner,
    TransportMethod:
      formData.TransportMethod === "Other"
        ? formData.TransportMethodOther
        : formData.TransportMethod,
    CollarSize: formData.CollarSize === "Other" ? formData.CollarSizeOther : formData.CollarSize,
    ECName: formData.ECName,
    ECAddress: formData.ECAddress,
    ECMobile: formData.ECMobile,
    ECRelationship: formData.ECRelationship,
    SpouseFullName: formData.SpouseFullName,
    SpouseOccupation: formData.SpouseOccupation,
    SpouseBirthDate: formData.SpouseBirthDate || null,
    FatherName: formData.FatherName,
    FatherDeceased: formData.FatherDeceased ? 1:0,
    MotherName: formData.MotherName,
    MotherDeceased: formData.MotherDeceased ? 1:0,
    SpouseFatherName: formData.FatherInLawName,
    FatherInLawDeceased: formData.FatherInLawDeceased ? 1:0,
    SpouseFatherDeceased: formData.FatherInLawDeceased ? 1:0,
    SpouseMotherName: formData.MotherInLawName,
    MotherInLawDeceased: formData.MotherInLawDeceased,
    SpouseMotherDeceased: formData.MotherInLawDeceased,
    MedicalConditions: (() => {
      const items = formData.Sickness.includes("Other")
        ? [...formData.Sickness.filter((s) => s !== "Other"), ...(formData.SicknessOther ? [formData.SicknessOther] : [])]
        : formData.Sickness;
      return items.length > 0 ? items.join(", ") : "None";
    })(),
    MedicalDescription: formData.SicknessDetails,
    NewOld: formData.NewOld,
    Remark: formData.VehicleTypeOwned,
    DepartmentCode: formData.DepartmentCode,
    LocationCode: formData.LocationCode,
    Roster: formData.Roster,
    // ── New fields — using exact backend field names ──
    DutyType: formData.DutyType,
    ExternalType: formData.DutyType === "External" ? formData.ExternalType : "",
    SubDuty: formData.SubDutyTraditional,
    Payrolltype: formData.PayRollType,
    Designation: formData.Designation,
    WorkLocation: formData.WorkLocation,
    Division: formData.Division,
    HTown: formData.HomeTown,
    TransportM1: formData.TransportMethod === "Other" ? formData.TransportMethodOther : formData.TransportMethod,
    TransportM2: Array.isArray(formData.TransportMethod2) ? formData.TransportMethod2.join(", ") : formData.TransportMethod2,
    BEmployment: formData.BasisOfEmployment,
    EmploymentEndDate: formData.BasisOfEmployment === "Assignment" ? (formData.AssignmentEndDate || null) : null,
    WorkCat: formData.WorkCategory,
    EmploymentCategoryID: 1, // every employee uses category 1; the leave policies are linked to it
    Grade: formData.Grade,
    Shift: formData.ApplicableShifts,
    LeaveDate: formData.LeavingDate || null,
    LeaveReason: formData.LeavingReason,
    AttendenceID: formData.AttendanceID || "0",
  });

  // ── Build combined dependents rows: Children + Father/Mother/FIL/MIL ──
  // Merges in disability info from the "familyIllnesses" section by matching Name,
  // so the same person only needs to be entered once for disability to attach correctly.
  const buildDependentsPayload = (serviceNo, childrenArr, coreData, illnessArr) => {
    const findIllness = (name) => {
      if (!name) return null;
      const target = name.trim().toLowerCase();
      return (illnessArr || []).find(
        (fi) => (fi.Name || "").trim().toLowerCase() === target
      ) || null;
    };

    // Children — Sex/School/Grade only apply here, since only the Children
    // section has actual textfields for them.
    const childRows = (childrenArr || []).map((child) => {
      const match = findIllness(child.Name);
      return {
        Id: child.DependentId || null,
        ServiceNo: serviceNo,
        Name: child.Name,
        Relationship: "Child",
        Sex: child.Sex,
        BirthDate: child.BirthDate,
        MaritalStatus: "Single",
        Employed: false,
        Disabled: !!match,
        Deceased: false,
        DeceasedDate: null,
        Code: "",
        MedicalStatus: "Healthy",
        NICNo: "",
        Remarks: "",
        DeathRemarks: "",
        Institue: child.School,
        EduLevel: child.Grade,
        DisRemark: match ? match.Illness : "",
      };
    });

    // Father / Mother / Father-in-Law / Mother-in-Law — only Name + Deceased
    // checkbox exist as actual fields for these, so that's all we send.
    const parentDefs = [
      { name: coreData.FatherName,      deceased: coreData.FatherDeceased,      rel: "Father", id: coreData.FatherDependentId },
      { name: coreData.MotherName,      deceased: coreData.MotherDeceased,      rel: "Mother", id: coreData.MotherDependentId },
      { name: coreData.FatherInLawName, deceased: coreData.FatherInLawDeceased, rel: "FIL",     id: coreData.FatherInLawDependentId },
      { name: coreData.MotherInLawName, deceased: coreData.MotherInLawDeceased, rel: "MIL",     id: coreData.MotherInLawDependentId },
    ];

    const parentRows = parentDefs
      .filter((p) => (p.name || "").trim() !== "")
      .map((p) => {
        const match = findIllness(p.name);
        return {
          Id: p.id || null,
          ServiceNo: serviceNo,
          Name: p.name,
          Relationship: p.rel,
          MaritalStatus: "",
          Employed: false,
          Disabled: !!match,
          Deceased: !!p.deceased,
          DeceasedDate: null,
          Code: "",
          NICNo: "",
          Remarks: "",
          DeathRemarks: "",
          DisRemark: match ? match.Illness : "",
        };
      });

    return [...childRows, ...parentRows];
  };

  // ── Submit all APIs for one employee ─────────────────────
  const submitEmployeeAPIs = async (serviceNo) => {
    // API 1 — main employee info
    await dispatch(postEmployeeDetails([buildEmployeePayload(serviceNo)]));

    // API 2 — qualifications
    await dispatch(
      postEmployeeQualifications([
        {
          ServiceNo: serviceNo,
          EducationalQualification:
            formData.HighestEducation === "Other"
              ? formData.HighestEducationOther
              : formData.HighestEducation,
          ProfessionalQualification: (() => {
            const items = formData.OtherQualifications.includes("Other")
              ? [...formData.OtherQualifications.filter((q) => q !== "Other"), ...(formData.OtherQualificationsOther ? [formData.OtherQualificationsOther] : [])]
              : formData.OtherQualifications;
            return items.join(", ");
          })(),
          SkillsAndAbilities: (() => {
            const items = formData.OtherSkills.includes("Other")
              ? [...formData.OtherSkills.filter((s) => s !== "Other"), ...(formData.OtherSkillsOther ? [formData.OtherSkillsOther] : [])]
              : formData.OtherSkills;
            return items.join(", ");
          })(),
        },
      ])
    );

    // API 3 — dependents (children + parents/in-laws, with disability merged in)
    const dependentsPayload = buildDependentsPayload(serviceNo, children, formData, familyIllnesses);
    if (dependentsPayload.length > 0) {
      await dispatch(postEmployeeDependents(dependentsPayload));
    }

    // API 4 — previous employment
    if (prevEmployments.length > 0) {
      await dispatch(
        postPreviousEmployment(
          prevEmployments.map((job) => ({
            ServiceNo: serviceNo,
            Company: job.Institute,
            Designation: job.Position,
            Duration: job.From && job.To ? `${job.From} to ${job.To}` : "",
            ResignationReason: job.LeavingReason,
          }))
        )
      );
    }

    // API 5 — workplace relationships
    if (relatives.length > 0) {
      await dispatch(
        postWorkplaceRelationships(
          relatives.map((rel) => ({
            ServiceNo: serviceNo,
            Name: rel.Name,
            Relationship: rel.Relationship,
          }))
        )
      );
    }

    // API 6 — monthly payment (only rows that are new or edited; rows loaded from the server are skipped)
    if (monthlyPayments.some((row) => !row.fromServer)) {
      await dispatch(
        postPayment(
          monthlyPayments.filter((row) => !row.fromServer).map((row) => ({
            Id: row.serverId || 0,
            ServiceNo: serviceNo,
            Allowance: parseFloat(row.Allowance) || 0,
            Date: row.Year ? `${row.Year}-01-01` : new Date().toISOString().slice(0, 10),
            AdditionalAllowance: parseFloat(row.AdditionalAllowance) || 0,
            TotalAllowance: parseFloat(row.Total) || 0,
          }))
        )
      );
    }

    // API 7 — welfare benefits
    if (welfareRecords.length > 0) {
      await dispatch(
        postWelfareBenefits(
          welfareRecords.map((rec) => ({
            ServiceNo: serviceNo,
            Date: rec.Date,
            Category: rec.Category,
            Description: rec.Description,
            ValueGrant: parseFloat(rec.ValueOfGrant) || 0,
          }))
        )
      );
    }

    // API 8 — other important information
    if (importantInfoRecords.length > 0) {
      await dispatch(
        postImportantInformation(
          importantInfoRecords.map((info) => ({
            ServiceNo: serviceNo,
            Description: info.Description,
            Date: info.Date,
            Remarks: info.Remarks,
          }))
        )
      );
    }
  };

  // ── Direct register & save ────────────────────────────────
const handleDirectRegister = async () => {
    if (isSubmittingRef.current) return;
    if (!validateForm()) {
      setShowDirectDialog(false); 
      setSnackbar({ open: true, message: "Please fill all required fields", severity: "error" });
      return;
    }
    isSubmittingRef.current = true;
    setSubmitting(true);
    try {
      const serviceNo = formData.ServiceNo;
      await submitEmployeeAPIs(serviceNo);

      setSnackbar({
        open: true,
        message: "Employee registered successfully!",
        severity: "success",
      });

      // Add to session list as Saved so it appears immediately in the panel
      setSessionEmployees((prev) => [
        {
          id: Date.now(),
          ServiceNo: formData.ServiceNo,
          ReportName: formData.ReportName,
          MobileNo: formData.MobileNo,
          statusType: "Saved",
          // Store full form + dynamic lists so the user can re-edit from the panel
          fullData: { ...formData },
          fullChildren: [...children],
          fullPrevEmployments: [...prevEmployments],
          fullRelatives: [...relatives],
          fullFamilyIllnesses: [...familyIllnesses],
          fullMonthlyPayments: monthlyPayments.map((r) => ({ ...r, fromServer: true })),
          fullWelfareRecords: [...welfareRecords],
          fullImportantInfoRecords: [...importantInfoRecords],
        },
        ...prev,
      ]);

      setShowDirectDialog(false);

      // Small delay to allow DB to commit before re-fetching the max service number
      await new Promise((resolve) => setTimeout(resolve, 500));

      handleClearForm();
      dispatch(getAllEmployees());
    } catch (error) {
      console.error("Registration error:", error);
      setShowDirectDialog(false);
      setSnackbar({
        open: true,
        message: error.message || "Error registering employee",
        severity: "error",
      });
    } finally {
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleAddEmployeeToQueue = async () => {
    if (!validateForm()) {
      setSnackbar({ open: true, message: "Please fill all required fields", severity: "error" });
      return;
    }
    const mobileExists = sessionEmployees.some(
      (emp) => emp.MobileNo === formData.MobileNo
    );
    if (mobileExists && editingId === null) {
      setSnackbar({
        open: true,
        message: "Mobile number already exists in this session",
        severity: "error",
      });
      return;
    }
    if (editingId !== null) {
      setSessionEmployees((prev) =>
        prev.map((emp) =>
          emp.id === editingId
            ? {
                ...emp,
                ServiceNo: formData.ServiceNo,
                ReportName: formData.ReportName,
                MobileNo: formData.MobileNo,
                fullData: { ...formData },
                fullChildren: [...children],
                fullPrevEmployments: [...prevEmployments],
                fullRelatives: [...relatives],
                fullFamilyIllnesses: [...familyIllnesses],
                fullMonthlyPayments: [...monthlyPayments],
                fullWelfareRecords: [...welfareRecords],
                fullImportantInfoRecords: [...importantInfoRecords],
              }
            : emp
        )
      );
      setEditingId(null);
    } else {
      setSessionEmployees((prev) => [
        {
          id: Date.now(),
          ServiceNo: formData.ServiceNo,
          ReportName: formData.ReportName,
          MobileNo: formData.MobileNo,
          statusType: "Queued",
          fullData: { ...formData },
          fullChildren: [...children],
          fullPrevEmployments: [...prevEmployments],
          fullRelatives: [...relatives],
          fullFamilyIllnesses: [...familyIllnesses],
          fullMonthlyPayments: [...monthlyPayments],
          fullWelfareRecords: [...welfareRecords],
          fullImportantInfoRecords: [...importantInfoRecords],
        },
        ...prev,
      ]);
    }
    handleClearForm();
  };

  // ── Batch submit all queued ───────────────────────────────
  const handleSubmitAll = async () => {
    const queued = sessionEmployees.filter((emp) => emp.statusType === "Queued");
    if (queued.length === 0) return;
    setSubmitting(true);
    try {
      let count = 0;
      for (const emp of queued) {
        // Temporarily load this employee's data so submitEmployeeAPIs uses it
        const savedFormData = { ...formData };
        const savedChildren = [...children];
        const savedPrevEmployments = [...prevEmployments];
        const savedRelatives = [...relatives];

        // Override module-level state references inside the loop
        // by calling the API directly with the stored data
        const data = emp.fullData || emp;

        await dispatch(postEmployeeDetails([buildEmployeePayloadFromData(emp.ServiceNo, data)]));

        await dispatch(
          postEmployeeQualifications([
            {
              ServiceNo: emp.ServiceNo,
              EducationalQualification:
                data.HighestEducation === "Other"
                  ? data.HighestEducationOther
                  : data.HighestEducation,
              ProfessionalQualification: (() => {
                const quals = data.OtherQualifications || [];
                const items = quals.includes("Other")
                  ? [...quals.filter((q) => q !== "Other"), ...(data.OtherQualificationsOther ? [data.OtherQualificationsOther] : [])]
                  : quals;
                return items.join(", ");
              })(),
              SkillsAndAbilities: (() => {
                const skills = data.OtherSkills || [];
                const items = skills.includes("Other")
                  ? [...skills.filter((s) => s !== "Other"), ...(data.OtherSkillsOther ? [data.OtherSkillsOther] : [])]
                  : skills;
                return items.join(", ");
              })(),
            },
          ])
        );

        const batchDependentsPayload = buildDependentsPayload(
          emp.ServiceNo,
          emp.fullChildren || [],
          data,
          emp.fullFamilyIllnesses || []
        );
        if (batchDependentsPayload.length > 0) {
          await dispatch(postEmployeeDependents(batchDependentsPayload));
        }

        if ((emp.fullPrevEmployments || []).length > 0) {
          await dispatch(
            postPreviousEmployment(
              emp.fullPrevEmployments.map((job) => ({
                ServiceNo: emp.ServiceNo,
                Company: job.Institute,
                Designation: job.Position,
                Duration: job.From && job.To ? `${job.From} to ${job.To}` : "",
                ResignationReason: job.LeavingReason,
              }))
            )
          );
        }

        if ((emp.fullRelatives || []).length > 0) {
          await dispatch(
            postWorkplaceRelationships(
              emp.fullRelatives.map((rel) => ({
                ServiceNo: emp.ServiceNo,
                Name: rel.Name,
                Relationship: rel.Relationship,
              }))
            )
          );
        }

        if ((emp.fullMonthlyPayments || []).length > 0) {
          await dispatch(
            postPayment(
              emp.fullMonthlyPayments.map((row) => ({
                ServiceNo: emp.ServiceNo,
                Allowance: parseFloat(row.Allowance) || 0,
                Date: row.Year ? `${row.Year}-01-01` : new Date().toISOString().slice(0, 10),
                AdditionalAllowance: parseFloat(row.AdditionalAllowance) || 0,
                TotalAllowance: parseFloat(row.Total) || 0,
              }))
            )
          );
        }

        if ((emp.fullWelfareRecords || []).length > 0) {
          await dispatch(
            postWelfareBenefits(
              emp.fullWelfareRecords.map((rec) => ({
                ServiceNo: emp.ServiceNo,
                Date: rec.Date,
                Category: rec.Category,
                Description: rec.Description,
                ValueGrant: parseFloat(rec.ValueOfGrant) || 0,
              }))
            )
          );
        }

        if ((emp.fullImportantInfoRecords || []).length > 0) {
          await dispatch(
            postImportantInformation(
              emp.fullImportantInfoRecords.map((info) => ({
                ServiceNo: emp.ServiceNo,
                Description: info.Description,
                Date: info.Date,
                Remarks: info.Remarks,
              }))
            )
          );
        }

        count++;
        setSessionEmployees((prev) =>
          prev.map((item) =>
            item.id === emp.id
              ? {
                  ...item,
                  statusType: "Saved",
                  fullMonthlyPayments: (item.fullMonthlyPayments || []).map((r) => ({ ...r, fromServer: true })),
                }
              : item
          )
        );
      }

  //     setSnackbar({
  //       open: true,
  //       message: `Successfully registered ${count} employee(s)!`,
  //       severity: "success",
  //     });
  //     setShowSubmitDialog(false);
  //     dispatch(getAllEmployees());
  //   } catch (error) {
  //     console.error("Batch submission error:", error);
  //     setSnackbar({ open: true, message: "Batch submission error", severity: "error" });
  //   } finally {
  //     setSubmitting(false);
  //   }
  // };

        setSnackbar({
        open: true,
        message: `Successfully registered ${count} employee(s)!`,
        severity: "success",
      });
      setShowSubmitDialog(false);
      dispatch(getAllEmployees());
      await refreshServiceNumber(true, serviceNoType);
    } catch (error) {
      console.error("Batch submission error:", error);
      setShowSubmitDialog(false);
      setSnackbar({ open: true, message: "Batch submission error", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };    

  // Helper: build payload from an arbitrary data object (for batch submit)
  const buildEmployeePayloadFromData = (serviceNo, data) => ({
    ServiceNo: serviceNo,
    RecruitmentDate: data.RecruitmentDate,
    ReportName: data.ReportName,
    FullName: data.FullName,
    FirstName: data.FirstName,
    HomeAddress: data.HomeAddress,
    HomeDistance: parseFloat(data.HomeDistance) || 0,
    MobileNo: data.MobileNo,
    HomeTelephone: data.HomeTelephone,
    GramaSevakaArea: data.GramaSevakaArea,
    DSDivision: data.DSDivision,
    PoliceArea: data.PoliceArea,
    ElectoralDivision: data.ElectoralDivision,
    TemporaryAddress: data.TemporaryAddress,
    TemporaryDistance: parseFloat(data.TemporaryDistance) || 0,
    BirthDate: data.BirthDate,
    NIDNo: data.NIDNo,
    MaritalStatus: data.MaritalStatus,
    BloodGroup: data.BloodGroup,
    LicenceNo: data.LicenceNo,
    OwnsHouse: data.OwnsHouse === "Yes",
    OwnsVehicle: data.OwnsVehicle === "Yes",
    VehicleType: data.VehicleType,
    VehicleNumber: data.VehicleNumber,
    VehicleRegisterOwner: data.VehicleRegisterOwner,
    TransportMethod:
      data.TransportMethod === "Other"
        ? data.TransportMethodOther
        : data.TransportMethod,
    CollarSize: data.CollarSize === "Other" ? data.CollarSizeOther : data.CollarSize,
    ECName: data.ECName,
    ECAddress: data.ECAddress,
    ECMobile: data.ECMobile,
    ECRelationship: data.ECRelationship,
    SpouseFullName: data.SpouseFullName,
    SpouseOccupation: data.SpouseOccupation,
    SpouseBirthDate: data.SpouseBirthDate || null,
    FatherName: data.FatherName,
    FatherDeceased: data.FatherDeceased ? 1:0 ,
    MotherName: data.MotherName,
    MotherDeceased: data.MotherDeceased? 1:0,
    SpouseFatherName: data.FatherInLawName,
    FatherInLawDeceased: data.FatherInLawDeceased ? 1:0,
    SpouseFatherDeceased: data.FatherInLawDeceased ? 1:0,
    SpouseMotherName: data.MotherInLawName,
    MotherInLawDeceased: data.MotherInLawDeceased ? 1:0,
    SpouseMotherDeceased: data.MotherInLawDeceased ? 1:0,
    MedicalConditions: (() => {
      const sickList = data.Sickness || [];
      const items = sickList.includes("Other")
        ? [...sickList.filter((s) => s !== "Other"), ...(data.SicknessOther ? [data.SicknessOther] : [])]
        : sickList;
      return items.length > 0 ? items.join(", ") : "None";
    })(),
    MedicalDescription: data.SicknessDetails,
    NewOld: data.NewOld,
    Remark: data.VehicleTypeOwned,
    DepartmentCode: data.DepartmentCode,
    LocationCode: data.LocationCode,
    Roster: data.Roster,
    // ── New fields — using exact backend field names ──
    DutyType: data.DutyType,
    ExternalType: data.DutyType === "External" ? data.ExternalType : "",
    SubDuty: data.SubDutyTraditional,
    Payrolltype: data.PayRollType,
    Designation: data.Designation,
    WorkLocation: data.WorkLocation,
    Division: data.Division,
    HTown: data.HomeTown,
    TransportM1: data.TransportMethod === "Other" ? data.TransportMethodOther : data.TransportMethod,
    TransportM2: Array.isArray(data.TransportMethod2) ? data.TransportMethod2.join(", ") : data.TransportMethod2,
    BEmployment: data.BasisOfEmployment,
    EmploymentEndDate: data.BasisOfEmployment === "Assignment" ? (data.AssignmentEndDate || null) : null,
    WorkCat: data.WorkCategory,
    EmploymentCategoryID: 1, // every employee uses category 1; the leave policies are linked to it
    Grade: data.Grade,
    Shift: data.ApplicableShifts,
    LeaveDate: data.LeavingDate || null,
    LeaveReason: data.LeavingReason,
    AttendenceID: data.AttendanceID || "0",
  });

  // ── Edit an employee from the session panel ───────────────
// Normalise any date value → "YYYY/MM/DD" string (for display & API submission)
// Handles: "1978/09/13", "10/1/2015 12:00:00 AM", "2015-10-01", ISO strings, Date objects
const safeDate = (val) => {
  if (!val) return "";

  const s = String(val).trim();

  // Already in YYYY/MM/DD format
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(s)) return s;

  // YYYY-MM-DD (HTML date input native)
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}/${isoMatch[2]}/${isoMatch[3]}`;

  // M/D/YYYY [time] — e.g. "10/1/2015 12:00:00 AM"
  const mdyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mdyMatch) {
    if (parseInt(mdyMatch[3], 10) <= 1900) return ""; // reject .NET default 1/1/1900
    const mm = String(mdyMatch[1]).padStart(2, "0");
    const dd = String(mdyMatch[2]).padStart(2, "0");
    return `${mdyMatch[3]}/${mm}/${dd}`;
  }

  // Fallback: try JS Date parse
  const d = new Date(val);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1900) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}/${mm}/${dd}`;
};

// const handleEditEmployee = async (employee) => {
//   const serviceNo = employee.ServiceNo || employee.serviceNo;
//   if (!serviceNo) return;

//   setTabValue(0);
//   setEditingId(employee.id);
//   window.scrollTo({ top: 0, behavior: "smooth" });

//   try {
//     // 1 ── Main employee details
//     const detailsResponse = await dispatch(getEmployeeByServiceNo(serviceNo));
//     // API returns camelCase fields — handle both camelCase and PascalCase
//     const e = detailsResponse?.payload?.resultSet?.[0] || {};

//     setFormData((prev) => ({
//       ...prev,
//       ServiceNo:            e.serviceNo            || e.ServiceNo            || serviceNo,
//       RecruitmentDate:      safeDate(e.recruitmentDate    || e.RecruitmentDate),
//       ReportName:           e.reportName           || e.ReportName           || "",
//       FullName:             e.fullName             || e.FullName             || "",
//       FirstName:            e.firstName            || e.FirstName            || "",
//       HomeAddress:          e.homeAddress          || e.HomeAddress          || "",
//       HomeDistance:         e.homeDistance         || e.HomeDistance         || "",
//       MobileNo:             e.mobileNo             || e.MobileNo             || "",
//       HomeTelephone:        e.homeTelephone        || e.HomeTelephone        || "",
//       GramaSevakaArea:      e.gramaSevakaArea      || e.GramaSevakaArea      || "",
//       DSDivision:           e.dsDivision           || e.DSDivision           || "",
//       PoliceArea:           e.policeArea           || e.PoliceArea           || "",
//       ElectoralDivision:    e.electoralDivision    || e.ElectoralDivision    || "",
//       TemporaryAddress:     e.temporaryAddress     || e.TemporaryAddress     || "",
//       TemporaryDistance:    e.temporaryDistance    || e.TemporaryDistance    || "",
//       BirthDate:            safeDate(e.birthDate          || e.BirthDate),
//       NIDNo:                e.nidNo                || e.NIDNo                || "",
//       MaritalStatus:        e.maritalStatus        || e.MaritalStatus        || "Single",
//       BloodGroup:           e.bloodGroup           || e.BloodGroup           || "",
//       LicenceNo:            e.licenceNo            || e.LicenceNo            || "",
//       OwnsHouse:            (e.ownsHouse === true   || e.OwnsHouse === true)   ? "Yes" : "No",
//       OwnsVehicle:          (e.ownsVehicle === true || e.OwnsVehicle === true) ? "Yes" : "No",
//       VehicleType:          e.vehicleType          || e.VehicleType          || "",
//       VehicleNumber:        e.vehicleNumber        || e.VehicleNumber        || "",
//       VehicleRegisterOwner: e.vehicleRegisterOwner || e.VehicleRegisterOwner || "",
//       TransportMethod:      e.transportMethod      || e.TransportMethod      || "",
//       CollarSize:           e.collarSize           || e.CollarSize           || "",
//       ECName:               e.ecName               || e.ECName               || "",
//       ECAddress:            e.ecAddress            || e.ECAddress            || "",
//       ECMobile:             e.ecMobile             || e.ECMobile             || "",
//       ECRelationship:       e.ecRelationship       || e.ECRelationship       || "",
//       SpouseFullName:       e.spouseFullName       || e.SpouseFullName       || "",
//       SpouseOccupation:     e.spouseOccupation     || e.SpouseOccupation     || "",
//       SpouseBirthDate:      safeDate(e.spouseBirthDate    || e.SpouseBirthDate),
//       FatherName:           e.fatherName           || e.FatherName           || "",
//       MotherName:           e.motherName           || e.MotherName           || "",
//       FatherInLawName:      e.spouseFatherName     || e.SpouseFatherName     || "",
//       MotherInLawName:      e.spouseMotherName     || e.SpouseMotherName     || "",
//       Sickness: (() => {
//         const raw = e.medicalConditions || e.MedicalConditions || "";
//         return raw && raw !== "None" ? raw.split(", ").filter(Boolean) : [];
//       })(),
//       SicknessDetails:      e.medicalDescription   || e.MedicalDescription   || "",
//       DepartmentCode:       e.departmentCode       || e.DepartmentCode       || "",
//       LocationCode:         e.locationCode         || e.LocationCode         || "",
//       Roster:               e.roster               || e.Roster               || "A",
//       NewOld:               e.newOld               || e.NewOld               || "N",
//       Remark:               e.remark               || e.Remark               || "",
//     }));

//     // 2 ── Qualifications
//     const qualResponse = await dispatch(getEmployeeQualifications(serviceNo));
//     const q = qualResponse?.payload?.resultSet?.[0] || {};

//     const eduRaw = q.educationalQualification || q.EducationalQualification || "";
//     const isOtherEdu = eduRaw !== "" && !educationOptions.includes(eduRaw);
//     const profRaw = q.professionalQualification || q.ProfessionalQualification || "";
//     const skillsRaw = q.skillsAndAbilities || q.SkillsAndAbilities || "";

//     setFormData((prev) => ({
//       ...prev,
//       HighestEducation:          isOtherEdu ? "Other" : eduRaw,
//       HighestEducationOther:     isOtherEdu ? eduRaw : "",
//       OtherQualifications:       profRaw ? profRaw.split(", ").filter(Boolean) : [],
//       OtherSkills:               skillsRaw ? skillsRaw.split(", ").filter(Boolean) : [],
//     }));

//     // 3 ── Dependents (children)
//     const depsResponse = await dispatch(getEmployeeDependents(serviceNo));
//     const deps = depsResponse?.payload?.resultSet || [];

//     setChildren(deps.map((d) => ({
//       id:        d.code          || d.Code          || Math.random(),
//       Name:      d.name          || d.Name          || "",
//       BirthDate: safeDate(d.birthDate || d.BirthDate),
//       Sex:       d.sex           || d.Sex           || "",
//       School:    d.medicalStatus || d.MedicalStatus || "",
//       Grade:     d.remarks       || d.Remarks       || "",
//     })));

//     // 4 ── Previous Employment
//     const prevEmpResponse = await dispatch(getPreviousEmployment(serviceNo));
//     const prevEmps = prevEmpResponse?.payload?.resultSet || [];

//     setPrevEmployments(prevEmps.map((p) => ({
//       id:            Math.random(),
//       Institute:     p.company           || p.Company           || "",
//       Position:      p.designation       || p.Designation       || "",
//       From:          (p.duration || p.Duration || "").split(" to ")[0] || "",
//       To:            (p.duration || p.Duration || "").split(" to ")[1] || "",
//       LeavingReason: p.resignationReason || p.ResignationReason || "",
//     })));

//     // 5 ── Workplace Relationships (relatives)
//     const relResponse = await dispatch(getWorkplaceRelationships(serviceNo));
//     const rels = relResponse?.payload?.resultSet || [];

//     setRelatives(rels.map((r) => ({
//       id:           Math.random(),
//       Name:         r.name         || r.Name         || "",
//       ServiceNo:    r.serviceNo    || r.ServiceNo    || "",
//       Relationship: r.relationship || r.Relationship || "",
//     })));

//   } catch (error) {
//     console.error("Error loading employee data for edit:", error);
//     setSnackbar({
//       open: true,
//       message: "Failed to load employee details",
//       severity: "error",
//     });
//   }
// };
const handleEditEmployee = async (employee) => {
  const serviceNo = employee.ServiceNo || employee.serviceNo;
  if (!serviceNo) return;

  setTabValue(0);
  setEditingId(employee.id);
  window.scrollTo({ top: 0, behavior: "smooth" });
  loadEmployeeImage(serviceNo);
  setImageFile(null);
  setImagePreviewUrl("");
  setImageUploadError("");

  // ── Restore session (locally queued/saved) employees directly ──
  if (employee.fullData) {
    setFormData({ ...employee.fullData });
    setServiceNoType(/^T/i.test(String(employee.fullData.ServiceNo || "").trim()) ? "T" : "normal");
    setChildren([...(employee.fullChildren || [])]);
    setPrevEmployments([...(employee.fullPrevEmployments || [])]);
    setRelatives([...(employee.fullRelatives || [])]);
    setFamilyIllnesses([...(employee.fullFamilyIllnesses || [])]);
    setMonthlyPayments([...(employee.fullMonthlyPayments || [])]);
    setWelfareRecords([...(employee.fullWelfareRecords || [])]);
    setImportantInfoRecords([...(employee.fullImportantInfoRecords || [])]);
    return;
  }

const resolveLovCode = (rawValue, options) => {
    if (!rawValue) return "";
    const match = (options || []).find(
      (opt) =>
        opt.code === rawValue ||
        (opt.name || "").toLowerCase() === String(rawValue).toLowerCase()
    );
    return match ? match.name : rawValue;
  };

//same lookup as above just resturn the code for the designation, worklocation, and division
//stores code interenally
const resolveLovCodeValue = (rawValue, options) => {
  if (!rawValue) return "";
  const match = (options || []).find(
    (opt) =>
      opt.code === rawValue || (opt.name || "").toLowerCase() === String(rawValue).toLowerCase()
  );
  return match ? match.code : rawValue;
};

  // Reads a field trying camelCase then PascalCase; returns null if absent
  const f = (obj, camel, pascal) => {
    if (obj[camel] !== undefined && obj[camel] !== null) return obj[camel];
    if (obj[pascal] !== undefined && obj[pascal] !== null) return obj[pascal];
    return null;
  };

  // Normalises any date value → "YYYY/MM/DD"; returns "" for missing/1900 defaults
  const safeDate = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(s)) return s;
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}/${isoMatch[2]}/${isoMatch[3]}`;
    // M/D/YYYY [time] — e.g. "6/18/2026 12:00:00 AM"
    const mdyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mdyMatch) {
      if (parseInt(mdyMatch[3], 10) <= 1900) return "";
      const mm = String(mdyMatch[1]).padStart(2, "0");
      const dd = String(mdyMatch[2]).padStart(2, "0");
      return `${mdyMatch[3]}/${mm}/${dd}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime()) || d.getFullYear() <= 1900) return "";
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
  };

  try {
    if (lovDataPromiseRef.current) {
      await lovDataPromiseRef.current;
    }

    // ── Fire all GET calls in parallel for speed ───────────────
    const [
      detailsResponse,
      qualResponse,
      depsResponse,
      prevEmpResponse,
      relResponse,
      paymentResponse,
      welfareResponse,
      importantInfoResponse,
    ] = await Promise.all([
      dispatch(getEmployeeByServiceNo(serviceNo)),
      dispatch(getEmployeeQualifications(serviceNo)),
      dispatch(getEmployeeDependents(serviceNo)),
      dispatch(getPreviousEmployment(serviceNo)),
      dispatch(getWorkplaceRelationships(serviceNo)),
      dispatch(getPayment(serviceNo)),
      dispatch(getWelfareBenefits(serviceNo)),
      dispatch(getImportantInformation(serviceNo)),
    ]);

    // ── Extract result sets ────────────────────────────────────
    const resultSet = detailsResponse?.payload?.resultSet || [];
    const emp = resultSet.find(
      (r) => String(r.ServiceNo || r.serviceNo) === String(serviceNo)
    ) || resultSet[0] || {};
    console.log("EDIT — matched emp:", JSON.stringify(emp));

    const qualSet = qualResponse?.payload?.resultSet || [];
    const qual = qualSet[0] || {};

    const deps    = depsResponse?.payload?.resultSet     || [];
    const prevEmps = prevEmpResponse?.payload?.resultSet || [];
    const rels    = relResponse?.payload?.resultSet      || [];
    const paymentRecords      = paymentResponse?.payload?.resultSet      || [];
    const welfareApiRecords   = welfareResponse?.payload?.resultSet      || [];
    const importantInfoApiRecords = importantInfoResponse?.payload?.resultSet || [];

    // ── Qualifications ─────────────────────────────────────────
    const eduRaw = f(qual, "educationalQualification", "EducationalQualification") || "";
    const isOtherEdu = eduRaw !== "" && !educationOptions.includes(eduRaw);

    const profRaw = f(qual, "professionalQualification", "ProfessionalQualification") || "";
    const profItems = profRaw ? profRaw.split(", ").filter(Boolean) : [];
    const knownProfItems = new Set(otherQualificationOptions.filter((o) => o !== "Other"));
    const profOtherText = profItems.find((item) => !knownProfItems.has(item) && item !== "Other") || "";
    const restoredOtherQuals = [
      ...profItems.filter((item) => knownProfItems.has(item)),
      ...(profOtherText ? ["Other"] : []),
    ];

    const skillsRaw = f(qual, "skillsAndAbilities", "SkillsAndAbilities") || "";
    const skillItems = skillsRaw ? skillsRaw.split(", ").filter(Boolean) : [];
    const knownSkillItems = new Set(otherSkillOptions.filter((o) => o !== "Other"));
    const skillOtherText = skillItems.find((item) => !knownSkillItems.has(item) && item !== "Other") || "";
    const restoredOtherSkills = [
      ...skillItems.filter((item) => knownSkillItems.has(item)),
      ...(skillOtherText ? ["Other"] : []),
    ];

    const medRaw = f(emp, "medicalConditions", "MedicalConditions") || "";
    const knownIllnessItems = new Set(illnessOptions.filter((o) => o !== "Other"));
    const medItems = medRaw && medRaw !== "None" ? medRaw.split(", ").filter(Boolean) : [];
    const sicknessOtherText = medItems.find((item) => !knownIllnessItems.has(item) && item !== "Other") || "";
    const restoredSickness = [
      ...medItems.filter((item) => knownIllnessItems.has(item)),
      ...(sicknessOtherText ? ["Other"] : []),
    ];

    // ── Resolve Service Number + detect T-type ──────────────────
    const resolvedServiceNo = String(f(emp, "serviceNo", "ServiceNo") ?? serviceNo);
    setServiceNoType(/^T/i.test(resolvedServiceNo.trim()) ? "T" : "normal");

    // ── Single setFormData call ────────────────────────────────
    setFormData((prev) => ({
      ...prev,
      // Core identity
      ServiceNo:            resolvedServiceNo,
      RecruitmentDate:      safeDate(f(emp, "recruitmentDate",    "RecruitmentDate")),
      ReportName:           f(emp, "reportName",           "ReportName")           ?? "",
      FullName:             f(emp, "fullName",             "FullName")             ?? "",
      FirstName:            f(emp, "firstName",            "FirstName")            ?? "",
      // Residence
      HomeAddress:          f(emp, "homeAddress",          "HomeAddress")          ?? "",
      HomeDistance:         f(emp, "homeDistance",         "HomeDistance")         ?? "",
      MobileNo:             f(emp, "mobileNo",             "MobileNo")             ?? "",
      HomeTelephone:        f(emp, "homeTelephone",        "HomeTelephone")        ?? "",
      GramaSevakaArea:      f(emp, "gramaSevakaArea",      "GramaSevakaArea")      ?? "",
      DSDivision:           f(emp, "dsDivision",           "DSDivision")           ?? "",
      PoliceArea:           f(emp, "policeArea",           "PoliceArea")           ?? "",
      ElectoralDivision:    f(emp, "electoralDivision",    "ElectoralDivision")    ?? "",
      TemporaryAddress:     f(emp, "temporaryAddress",     "TemporaryAddress")     ?? "",
      TemporaryDistance:    f(emp, "temporaryDistance",    "TemporaryDistance")    ?? "",
      // API returns "HTown" → maps to formData.HomeTown
      HomeTown:             f(emp, "hTown",                "HTown")                ?? "",
      // Personal
      BirthDate:            safeDate(f(emp, "birthDate",   "BirthDate")),
      NIDNo:                f(emp, "nidNo",                "NIDNo")                ?? "",
      MaritalStatus:        f(emp, "maritalStatus",        "MaritalStatus")        ?? "Single",
      BloodGroup:           f(emp, "bloodGroup",           "BloodGroup")           ?? "",
      LicenceNo:            f(emp, "licenceNo",            "LicenceNo")            ?? "",
      OwnsHouse:            f(emp, "ownsHouse",            "OwnsHouse")  === true  ? "Yes" : "No",
      OwnsVehicle:          f(emp, "ownsVehicle",          "OwnsVehicle") === true ? "Yes" : "No",
      VehicleType:          f(emp, "vehicleType",          "VehicleType")          ?? "",
      VehicleNumber:        f(emp, "vehicleNumber",        "VehicleNumber")        ?? "",
      VehicleRegisterOwner: f(emp, "vehicleRegisterOwner", "VehicleRegisterOwner") ?? "",
      // API has both "TransportMethod" (old) and "TransportM1" (new) — prefer TransportM1 if set
      TransportMethod: f(emp, "transportM1", "TransportM1") ?? "",
      TransportMethod2:     (f(emp, "transportM2", "TransportM2") || "").split(",").map((s) => s.trim()).filter(Boolean),
      // VehicleTypeOwned is stored in the backend's Remark column (no dedicated column exists)
      VehicleTypeOwned:     f(emp, "remark",               "Remark")               ?? "",
      CollarSize:           f(emp, "collarSize",           "CollarSize")           ?? "",
      // Emergency contact
      ECName:               f(emp, "ecName",               "ECName")               ?? "",
      ECAddress:            f(emp, "ecAddress",            "ECAddress")            ?? "",
      ECMobile:             f(emp, "ecMobile",             "ECMobile")             ?? "",
      ECRelationship:       f(emp, "ecRelationship",       "ECRelationship")       ?? "",
      // Spouse
      SpouseFullName:       f(emp, "spouseFullName",       "SpouseFullName")       ?? "",
      SpouseOccupation:     f(emp, "spouseOccupation",     "SpouseOccupation")     ?? "",
      SpouseBirthDate:      safeDate(f(emp, "spouseBirthDate", "SpouseBirthDate")),
      // Family
      FatherName:           f(emp, "fatherName",           "FatherName")           ?? "",
      MotherName:           f(emp, "motherName",           "MotherName")           ?? "",
      FatherInLawName:      f(emp, "spouseFatherName",     "SpouseFatherName")     ?? "",
      MotherInLawName:      f(emp, "spouseMotherName",     "SpouseMotherName")     ?? "",
      FatherDeceased:     !!f(emp, "fatherDeceased",       "FatherDeceased"),
      MotherDeceased:     !!f(emp, "motherDeceased",        "MotherDeceased"),
      FatherInLawDeceased: !!f(emp, "spouseFatherDeceased", "SpouseFatherDeceased"),
      MotherInLawDeceased: !!f(emp, "spouseMotherDeceased", "SpouseMotherDeceased"),
      // Medical
      Sickness:             restoredSickness,
      SicknessOther:        sicknessOtherText,
      SicknessDetails:      f(emp, "medicalDescription",   "MedicalDescription")   ?? "",
      // Admin
      DepartmentCode:       f(emp, "departmentCode",       "DepartmentCode")       ?? "",
      LocationCode:         f(emp, "locationCode",         "LocationCode")         ?? "",
      Roster:               f(emp, "roster",               "Roster")               ?? "A",
      NewOld:               (() => {
        const raw = String(f(emp, "newOld", "NewOld") ?? "").trim().toLowerCase();
        return raw === "inactive" || raw === "i" ? "Inactive" : "Active";
      })(),
      AttendanceID:         String(f(emp, "attendenceID",  "AttendenceID")         ?? ""),
      // Qualifications
      HighestEducation:         isOtherEdu ? "Other" : eduRaw,
      HighestEducationOther:    isOtherEdu ? eduRaw  : "",
      OtherQualifications:      restoredOtherQuals,
      OtherQualificationsOther: profOtherText,
      OtherSkills:              restoredOtherSkills,
      OtherSkillsOther:         skillOtherText,
      // ── Employment / Service Info fields ──────────────────
      // API field → formData field (confirmed from actual response):
      DutyType:          f(emp, "dutyType",          "DutyType")          ?? "",
      ExternalType:      f(emp, "externalType",       "ExternalType")      ?? "",
      SubDutyTraditional: f(emp, "subDuty", "SubDuty") ?? "",
      PayRollType:        f(emp, "payrolltype", "Payrolltype") ?? "",
      Designation:       resolveLovCodeValue(f(emp, "designation", "Designation") ?? "", designationOptionsRef.current),
      WorkLocation:      resolveLovCode(f(emp, "workLocation", "WorkLocation") ?? "", locationOptionsRef.current),
      Division:          resolveLovCodeValue(f(emp, "division", "Division") ?? "", divisionOptionsRef.current),
      BasisOfEmployment: f(emp, "bEmployment",        "BEmployment")       ?? "",
      AssignmentEndDate: safeDate(f(emp, "employmentEndDate", "EmploymentEndDate")),
      WorkCategory:      resolveLovCodeValue(f(emp, "workCat", "WorkCat") ?? "", workCategoryOptionsRef.current),
      Grade:             f(emp, "grade",              "Grade")             ?? "",
      ApplicableShifts:  f(emp, "shift",              "Shift")             ?? "",
      LeavingDate:       safeDate(f(emp, "leaveDate", "LeaveDate")),
      LeavingReason:     f(emp, "leaveReason",        "LeaveReason")       ?? "",
    }));

    // ── Children ───────────────────────────────────────────────
    const childDeps = deps.filter((d) => {
      const rel = (f(d, "relationship", "Relationship") || "").toLowerCase();
      return rel === "child" || rel === "";
    });
    setChildren(childDeps.map((d) => ({
      id:          f(d, "id", "Id") || Math.random(),
      DependentId: f(d, "id", "Id") ?? null,
      Name:      f(d, "name",          "Name")         ?? "",
      BirthDate: safeDate(f(d, "birthDate", "BirthDate")),
      Sex:       f(d, "sex",           "Sex")          ?? "",
      School:    f(d, "institue",      "Institue")     ?? "",
      Grade:     f(d, "eduLevel",      "EduLevel")     ?? "",
    })));

    // ── Father / Mother / FIL / MIL — Deceased status + row Id from dependents table ──
    const isTrue = (v) => v === true || v === 1 || String(v).toLowerCase() === "true" || String(v).trim() === "1";
    const findRel = (relTag) =>
      deps.find(
        (d) => (f(d, "relationship", "Relationship") || "").toLowerCase() === relTag.toLowerCase()
      );

    const fatherDep = findRel("Father");
    const motherDep = findRel("Mother");
    const filDep    = findRel("FIL");
    const milDep    = findRel("MIL");

    setFormData((prev) => ({
      ...prev,
      FatherDeceased:      fatherDep ? isTrue(f(fatherDep, "deceased", "Deceased")) : prev.FatherDeceased,
      MotherDeceased:      motherDep ? isTrue(f(motherDep, "deceased", "Deceased")) : prev.MotherDeceased,
      FatherInLawDeceased: filDep    ? isTrue(f(filDep,    "deceased", "Deceased")) : prev.FatherInLawDeceased,
      MotherInLawDeceased: milDep    ? isTrue(f(milDep,    "deceased", "Deceased")) : prev.MotherInLawDeceased,
      FatherDependentId:      fatherDep ? (f(fatherDep, "id", "Id") ?? null) : prev.FatherDependentId,
      MotherDependentId:      motherDep ? (f(motherDep, "id", "Id") ?? null) : prev.MotherDependentId,
      FatherInLawDependentId: filDep    ? (f(filDep,    "id", "Id") ?? null) : prev.FatherInLawDependentId,
      MotherInLawDependentId: milDep    ? (f(milDep,    "id", "Id") ?? null) : prev.MotherInLawDependentId,
    }));

    // ── Previous Employment ────────────────────────────────────
    setPrevEmployments(prevEmps.map((p) => {
      const dur = f(p, "duration", "Duration") || "";
      return {
        id:            Math.random(),
        Institute:     f(p, "company",           "Company")           ?? "",
        Position:      f(p, "designation",       "Designation")       ?? "",
        From:          dur.split(" to ")[0] || "",
        To:            dur.split(" to ")[1] || "",
        LeavingReason: f(p, "resignationReason", "ResignationReason") ?? "",
      };
    }));

    // ── Relatives ──────────────────────────────────────────────
    setRelatives(rels.map((r) => ({
      id:           Math.random(),
      Name:         f(r, "name",         "Name")         ?? "",
      ServiceNo:    f(r, "serviceNo",    "ServiceNo")    ?? "",
      Relationship: f(r, "relationship", "Relationship") ?? "",
    })));

    // ── Disability / Critical Illness of Family Members ─────────
    // Rebuilt from any dependent row (child or parent) marked Disabled.
    setFamilyIllnesses(
      deps
        .filter((d) => isTrue(f(d, "disabled", "Disabled")))
        .map((d) => ({
          id:           Math.random(),
          Name:         f(d, "name",         "Name")         ?? "",
          Relationship: f(d, "relationship", "Relationship") ?? "",
          Illness:      f(d, "disRemark",    "DisRemark")    ?? "",
        }))
    );

    // ── Monthly Payment ────────────────────────────────────────
    setMonthlyPayments(
      paymentRecords
      .filter((p, i, arr) => i === arr.findIndex((q) =>
        String(f(q, "date", "Date")) === String(f(p, "date", "Date")) &&
        Number(f(q, "allowance", "Allowance")) === Number(f(p, "allowance", "Allowance")) &&
        Number(f(q, "additionalAllowance", "AdditionalAllowance")) === Number(f(p, "additionalAllowance", "AdditionalAllowance"))
      ))
      .map((p) => ({
      id:                  Math.random(),
      fromServer:          true,
      serverId:            f(p, "id", "Id") ?? 0,
      // Date comes as "M/D/YYYY ..." — extract the year safely
      Year:                (() => {
        const d = f(p, "date", "Date") || "";
        // Try YYYY-... or YYYY/... first
        const y4 = d.match(/^(\d{4})[\/\-]/);
        if (y4) return y4[1];
        // M/D/YYYY format
        const mdy = d.match(/^\d{1,2}\/\d{1,2}\/(\d{4})/);
        if (mdy) return mdy[1];
        return new Date().getFullYear().toString();
      })(),
      Allowance:           String(f(p, "allowance",           "Allowance")           ?? ""),
      AdditionalAllowance: String(f(p, "additionalAllowance", "AdditionalAllowance") ?? ""),
      Total:               String(f(p, "totalAllowance",      "TotalAllowance")      ?? ""),
    })));

    // ── Welfare Benefits ───────────────────────────────────────
    // API returns "ValueGrant" (not "ValueOfGrant") — key fix here
    setWelfareRecords(welfareApiRecords.map((w) => ({
      id:           Math.random(),
      Date:         safeDate(f(w, "date",        "Date")),
      Category:     f(w, "category",    "Category")    ?? "",
      Description:  f(w, "description", "Description") ?? "",
      // API field is "ValueGrant", local state field is "ValueOfGrant"
      ValueOfGrant: String(f(w, "valueGrant", "ValueGrant") ?? ""),
    })));

    // ── Important Information ──────────────────────────────────
    setImportantInfoRecords(importantInfoApiRecords.map((info) => ({
      id:          f(info, "id",          "Id")          || Math.random(),
      Date:        safeDate(f(info, "date", "Date")),
      Description: f(info, "description", "Description") ?? "",
      Remarks:     f(info, "remarks",     "Remarks")     ?? "",
    })));

  } catch (error) {
    console.error("Error loading employee data for edit:", error);
    setSnackbar({
      open: true,
      message: "Failed to load employee details",
      severity: "error",
    });
  }
};
  const handleDeleteEmployee = (id) =>
    setSessionEmployees((prev) => prev.filter((emp) => emp.id !== id));

  // ── Refresh employees from API ────────────────────────────
  const handleRefreshEmployees = () => {
    dispatch(getAllEmployees());
    setSnackbar({ open: true, message: "Refreshing employee list...", severity: "info" });
  };

  //expor all the employee and their data into a excel
  const handleExportExcel = async () => {
  setSnackbar({ open: true, message: "Preparing export...", severity: "info" });

  // ── Helper to fetch full data for an API employee ──────
  const fetchFullData = async (serviceNo) => {
    const f = (obj, camel, pascal) => {
      if (obj[camel] !== undefined && obj[camel] !== null) return obj[camel];
      if (obj[pascal] !== undefined && obj[pascal] !== null) return obj[pascal];
      return null;
    };
    const safeVal = (v) => (v === null || v === undefined ? "" : v);

    const [
      detailsRes, qualRes, depsRes,
      prevEmpRes, relRes, payRes,
      welfareRes, infoRes,
    ] = await Promise.all([
      dispatch(getEmployeeByServiceNo(serviceNo)),
      dispatch(getEmployeeQualifications(serviceNo)),
      dispatch(getEmployeeDependents(serviceNo)),
      dispatch(getPreviousEmployment(serviceNo)),
      dispatch(getWorkplaceRelationships(serviceNo)),
      dispatch(getPayment(serviceNo)),
      dispatch(getWelfareBenefits(serviceNo)),
      dispatch(getImportantInformation(serviceNo)),
    ]);

    const emp  = detailsRes?.payload?.resultSet?.[0] || {};
    const qual = qualRes?.payload?.resultSet?.[0]    || {};
    const deps    = depsRes?.payload?.resultSet     || [];
    const prevEmps = prevEmpRes?.payload?.resultSet || [];
    const rels    = relRes?.payload?.resultSet      || [];
    const payments = payRes?.payload?.resultSet     || [];
    const welfare  = welfareRes?.payload?.resultSet || [];
    const info     = infoRes?.payload?.resultSet    || [];

    return { emp, qual, deps, prevEmps, rels, payments, welfare, info, f, safeVal };
  };

  try {
    // ── Collect full data for all displayed employees ──────
    const allData = [];

    for (const employee of filteredEmployees) {
      if (employee.fullData) {
        // Session employee — data already available
        const d = employee.fullData;
        allData.push({
          serviceNo: employee.ServiceNo,
          core: {
            ServiceNo: d.ServiceNo,
            ReportName: d.ReportName,
            FullName: d.FullName,
            FirstName: d.FirstName,
            RecruitmentDate: d.RecruitmentDate,
            BirthDate: d.BirthDate,
            NIDNo: d.NIDNo,
            MaritalStatus: d.MaritalStatus,
            BloodGroup: d.BloodGroup,
            MobileNo: d.MobileNo,
            HomeTelephone: d.HomeTelephone,
            HomeAddress: d.HomeAddress,
            HomeDistance: d.HomeDistance,
            HomeTown: d.HomeTown,
            GramaSevakaArea: d.GramaSevakaArea,
            DSDivision: d.DSDivision,
            PoliceArea: d.PoliceArea,
            ElectoralDivision: d.ElectoralDivision,
            TemporaryAddress: d.TemporaryAddress,
            TemporaryDistance: d.TemporaryDistance,
            DutyType: d.DutyType,
            ExternalType: d.ExternalType,
            Designation: d.Designation,
            WorkLocation: d.WorkLocation,
            Division: d.Division,
            BasisOfEmployment: d.BasisOfEmployment,
            AssignmentEndDate: d.AssignmentEndDate,
            WorkCategory: d.WorkCategory,
            Grade: d.Grade,
            ApplicableShifts: d.ApplicableShifts,
            LeavingDate: d.LeavingDate,
            LeavingReason: d.LeavingReason,
            LicenceNo: d.LicenceNo,
            OwnsHouse: d.OwnsHouse,
            OwnsVehicle: d.OwnsVehicle,
            VehicleType: d.VehicleType,
            VehicleNumber: d.VehicleNumber,
            VehicleRegisterOwner: d.VehicleRegisterOwner,
            TransportMethod: d.TransportMethod,
            TransportMethod2: d.TransportMethod2,
            VehicleTypeOwned: d.VehicleTypeOwned,
            CollarSize: d.CollarSize,
            ECName: d.ECName,
            ECMobile: d.ECMobile,
            ECRelationship: d.ECRelationship,
            ECAddress: d.ECAddress,
            SpouseFullName: d.SpouseFullName,
            SpouseOccupation: d.SpouseOccupation,
            SpouseBirthDate: d.SpouseBirthDate,
            FatherName: d.FatherName,
            FatherDeceased: d.FatherDeceased ? "Yes" : "No",
            MotherName: d.MotherName,
            MotherDeceased: d.MotherDeceased ? "Yes" : "No",
            FatherInLawName: d.FatherInLawName,
            FatherInLawDeceased: d.FatherInLawDeceased ? "Yes" : "No",
            MotherInLawName: d.MotherInLawName,
            MotherInLawDeceased: d.MotherInLawDeceased ? "Yes" : "No",
            Sickness: (d.Sickness || []).join(", "),
            SicknessDetails: d.SicknessDetails,
            HighestEducation: d.HighestEducation,
            OtherQualifications: (d.OtherQualifications || []).join(", "),
            OtherSkills: (d.OtherSkills || []).join(", "),
            DepartmentCode: d.DepartmentCode,
            LocationCode: d.LocationCode,
            Roster: d.Roster,
            NewOld: d.NewOld,
            Remark: d.Remark,
            AttendanceID: d.AttendanceID,
            Status: "Session",
          },
          deps:     employee.fullChildren        || [],
          prevEmps: employee.fullPrevEmployments || [],
          rels:     employee.fullRelatives       || [],
          payments: employee.fullMonthlyPayments || [],
          welfare:  employee.fullWelfareRecords  || [],
          info:     employee.fullImportantInfoRecords || [],
        });
      } else {
        // API employee — fetch all data
        const { emp, qual, deps, prevEmps, rels, payments, welfare, info, f, safeVal } =
          await fetchFullData(employee.ServiceNo);

        allData.push({
          serviceNo: employee.ServiceNo,
          core: {
            ServiceNo:          safeVal(f(emp, "serviceNo",            "ServiceNo")),
            ReportName:         safeVal(f(emp, "reportName",           "ReportName")),
            FullName:           safeVal(f(emp, "fullName",             "FullName")),
            FirstName:          safeVal(f(emp, "firstName",            "FirstName")),
            RecruitmentDate:    safeVal(f(emp, "recruitmentDate",      "RecruitmentDate")),
            BirthDate:          safeVal(f(emp, "birthDate",            "BirthDate")),
            NIDNo:              safeVal(f(emp, "nidNo",                "NIDNo")),
            MaritalStatus:      safeVal(f(emp, "maritalStatus",        "MaritalStatus")),
            BloodGroup:         safeVal(f(emp, "bloodGroup",           "BloodGroup")),
            MobileNo:           safeVal(f(emp, "mobileNo",             "MobileNo")),
            HomeTelephone:      safeVal(f(emp, "homeTelephone",        "HomeTelephone")),
            HomeAddress:        safeVal(f(emp, "homeAddress",          "HomeAddress")),
            HomeDistance:       safeVal(f(emp, "homeDistance",         "HomeDistance")),
            HomeTown:           safeVal(f(emp, "hTown",                "HTown")),
            GramaSevakaArea:    safeVal(f(emp, "gramaSevakaArea",      "GramaSevakaArea")),
            DSDivision:         safeVal(f(emp, "dsDivision",           "DSDivision")),
            PoliceArea:         safeVal(f(emp, "policeArea",           "PoliceArea")),
            ElectoralDivision:  safeVal(f(emp, "electoralDivision",    "ElectoralDivision")),
            TemporaryAddress:   safeVal(f(emp, "temporaryAddress",     "TemporaryAddress")),
            TemporaryDistance:  safeVal(f(emp, "temporaryDistance",    "TemporaryDistance")),
            DutyType:           safeVal(f(emp, "dutyType",             "DutyType")),
            ExternalType:       safeVal(f(emp, "externalType",         "ExternalType")),
            Designation:        safeVal(f(emp, "designation",          "Designation")),
            WorkLocation:       safeVal(f(emp, "workLocation",         "WorkLocation")),
            Division:           safeVal(f(emp, "division",             "Division")),
            BasisOfEmployment:  safeVal(f(emp, "bEmployment",          "BEmployment")),
            AssignmentEndDate:  safeVal(f(emp, "employmentEndDate",    "EmploymentEndDate")),
            WorkCategory:       safeVal(f(emp, "workCat",              "WorkCat")),
            Grade:              safeVal(f(emp, "grade",                "Grade")),
            ApplicableShifts:   safeVal(f(emp, "shift",                "Shift")),
            LeavingDate:        safeVal(f(emp, "leaveDate",            "LeaveDate")),
            LeavingReason:      safeVal(f(emp, "leaveReason",          "LeaveReason")),
            LicenceNo:          safeVal(f(emp, "licenceNo",            "LicenceNo")),
            OwnsHouse:          f(emp, "ownsHouse",   "OwnsHouse")  === true ? "Yes" : "No",
            OwnsVehicle:        f(emp, "ownsVehicle", "OwnsVehicle") === true ? "Yes" : "No",
            VehicleType:        safeVal(f(emp, "vehicleType",          "VehicleType")),
            VehicleNumber:      safeVal(f(emp, "vehicleNumber",        "VehicleNumber")),
            VehicleRegisterOwner: safeVal(f(emp, "vehicleRegisterOwner", "VehicleRegisterOwner")),
            TransportMethod:    safeVal(f(emp, "transportM1",          "TransportM1")),
            TransportMethod2:   safeVal(f(emp, "transportM2",          "TransportM2")),
            // VehicleTypeOwned is stored in the backend's Remark column
            VehicleTypeOwned:   safeVal(f(emp, "remark",               "Remark")),
            CollarSize:         safeVal(f(emp, "collarSize",           "CollarSize")),
            ECName:             safeVal(f(emp, "ecName",               "ECName")),
            ECMobile:           safeVal(f(emp, "ecMobile",             "ECMobile")),
            ECRelationship:     safeVal(f(emp, "ecRelationship",       "ECRelationship")),
            ECAddress:          safeVal(f(emp, "ecAddress",            "ECAddress")),
            SpouseFullName:     safeVal(f(emp, "spouseFullName",       "SpouseFullName")),
            SpouseOccupation:   safeVal(f(emp, "spouseOccupation",     "SpouseOccupation")),
            SpouseBirthDate:    safeVal(f(emp, "spouseBirthDate",      "SpouseBirthDate")),
            FatherName:         safeVal(f(emp, "fatherName",           "FatherName")),
            FatherDeceased:     !!f(emp, "fatherDeceased",   "FatherDeceased")  ? "Yes" : "No",
            MotherName:         safeVal(f(emp, "motherName",           "MotherName")),
            MotherDeceased:     !!f(emp, "motherDeceased",   "MotherDeceased")  ? "Yes" : "No",
            FatherInLawName:    safeVal(f(emp, "spouseFatherName",     "SpouseFatherName")),
            FatherInLawDeceased: !!f(emp, "spouseFatherDeceased", "SpouseFatherDeceased") ? "Yes" : "No",
            MotherInLawName:    safeVal(f(emp, "spouseMotherName",     "SpouseMotherName")),
            MotherInLawDeceased: !!f(emp, "spouseMotherDeceased", "SpouseMotherDeceased") ? "Yes" : "No",
            Sickness:           safeVal(f(emp, "medicalConditions",    "MedicalConditions")),
            SicknessDetails:    safeVal(f(emp, "medicalDescription",   "MedicalDescription")),
            HighestEducation:   safeVal(f(qual, "educationalQualification", "EducationalQualification")),
            OtherQualifications: safeVal(f(qual, "professionalQualification", "ProfessionalQualification")),
            OtherSkills:        safeVal(f(qual, "skillsAndAbilities",  "SkillsAndAbilities")),
            DepartmentCode:     safeVal(f(emp, "departmentCode",       "DepartmentCode")),
            LocationCode:       safeVal(f(emp, "locationCode",         "LocationCode")),
            Roster:             safeVal(f(emp, "roster",               "Roster")),
            NewOld:             safeVal(f(emp, "newOld",               "NewOld")),
            AttendanceID:       safeVal(f(emp, "attendenceID",         "AttendenceID")),
            Status: "Registered",
          },
          deps,
          prevEmps,
          rels,
          payments,
          welfare,
          info,
        });
      }
    }

    // ── Build workbook ─────────────────────────────────────
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Employee Core Data
    const coreRows = allData.map((d) => d.core);
    const wsCore = XLSX.utils.json_to_sheet(coreRows);
    XLSX.utils.book_append_sheet(wb, wsCore, "Employees");

    // Sheet 2 — Dependents (Children)
    const depRows = allData.flatMap((d) =>
      (d.deps || []).map((dep) => ({
        ServiceNo:    d.serviceNo,
        Name:         dep.Name         || dep.name         || "",
        BirthDate:    dep.BirthDate    || dep.birthDate     || "",
        Sex:          dep.Sex          || dep.sex           || "",
        School:       dep.School       || dep.medicalStatus || "",
        Grade:        dep.Grade        || dep.remarks       || "",
      }))
    );
    if (depRows.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(depRows), "Dependents");
    }

    // Sheet 3 — Previous Employment
    const prevRows = allData.flatMap((d) =>
      (d.prevEmps || []).map((p) => ({
        ServiceNo:     d.serviceNo,
        Institute:     p.Institute     || p.company      || p.Company      || "",
        Position:      p.Position      || p.designation  || p.Designation  || "",
        From:          p.From          || "",
        To:            p.To            || "",
        LeavingReason: p.LeavingReason || p.resignationReason || p.ResignationReason || "",
      }))
    );
    if (prevRows.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prevRows), "Prev Employment");
    }

    // Sheet 4 — Relatives
    const relRows = allData.flatMap((d) =>
      (d.rels || []).map((r) => ({
        ServiceNo:    d.serviceNo,
        Name:         r.Name         || r.name         || "",
        RelServiceNo: r.ServiceNo    || r.serviceNo    || "",
        Relationship: r.Relationship || r.relationship || "",
      }))
    );
    if (relRows.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(relRows), "Relatives");
    }

    // Sheet 5 — Monthly Payments
    const payRows = allData.flatMap((d) =>
      (d.payments || []).map((p) => ({
        ServiceNo:           d.serviceNo,
        Year:                p.Year          || p.year          || p.Date || p.date || "",
        Allowance:           p.Allowance     || p.allowance     || "",
        AdditionalAllowance: p.AdditionalAllowance || p.additionalAllowance || "",
        Total:               p.Total         || p.totalAllowance || p.TotalAllowance || "",
      }))
    );
    if (payRows.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(payRows), "Monthly Payments");
    }

    // Sheet 6 — Welfare Benefits
    const welfareRows = allData.flatMap((d) =>
      (d.welfare || []).map((w) => ({
        ServiceNo:    d.serviceNo,
        Date:         w.Date        || w.date        || "",
        Category:     w.Category    || w.category    || "",
        Description:  w.Description || w.description || "",
        ValueOfGrant: w.ValueOfGrant || w.valueGrant || w.ValueGrant || "",
      }))
    );
    if (welfareRows.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(welfareRows), "Welfare Benefits");
    }

    // Sheet 7 — Important Information
    const infoRows = allData.flatMap((d) =>
      (d.info || []).map((i) => ({
        ServiceNo:   d.serviceNo,
        Date:        i.Date        || i.date        || "",
        Description: i.Description || i.description || "",
        Remarks:     i.Remarks     || i.remarks     || "",
      }))
    );
    if (infoRows.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(infoRows), "Important Info");
    }

    // ── Download ───────────────────────────────────────────
    const fileName = `SDM_Employees_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);

    setSnackbar({ open: true, message: `Exported ${allData.length} employee(s) successfully!`, severity: "success" });

  } catch (error) {
    console.error("Export error:", error);
    setSnackbar({ open: true, message: "Export failed. Please try again.", severity: "error" });
  }
};

  // ── Clear entire form ─────────────────────────────────────
  const handleClearForm = (serviceNoToSet) => {
    setEditingId(null);
    setChildren([]);
    setPrevEmployments([]);
    setRelatives([]);
    setFamilyIllnesses([]);
    setMonthlyPayments([]);
    setWelfareRecords([]);
    setImportantInfoRecords([]);
    // Reset image
    setImageFile(null);
    setImagePreviewUrl("");
    setSavedImageUrl("");
    setImageUploadError("");

    if (serviceNoToSet == null) {
      refreshServiceNumber(true, serviceNoType);
    }
    setFormData({
      ServiceNo: "",
      RecruitmentDate: "",
      ReportName: "",
      FullName: "",
      FirstName: "",
      HomeAddress: "",
      HomeDistance: "",
      MobileNo: "",
      HomeTelephone: "",
      GramaSevakaArea: "",
      DSDivision: "",
      PoliceArea: "",
      ElectoralDivision: "",
      TemporaryAddress: "",
      TemporaryDistance: "",
      HighestEducation: "",
      HighestEducationOther: "",
      OtherQualifications: [],
      OtherQualificationsOther: "",
      OtherSkills: [],
      OtherSkillsOther: "",
      BirthDate: "",
      NIDNo: "",
      MaritalStatus: "Single",
      BloodGroup: "",
      LicenceNo: "",
      OwnsHouse: "No",
      OwnsVehicle: "No",
      VehicleType: "",
      VehicleNumber: "",
      VehicleRegisterOwner: "",
      TransportMethod: "",
      TransportMethodOther: "",
      CollarSize: "",
      CollarSizeOther: "",
      ECName: "",
      ECAddress: "",
      ECMobile: "",
      ECRelationship: "",
      SpouseFullName: "",
      SpouseOccupation: "",
      SpouseBirthDate: "",
      FatherName: "",
      FatherDeceased: false,
      FatherDependentId: null,
      MotherName: "",
      MotherDeceased: false,
      MotherDependentId: null,
      FatherInLawName: "",
      FatherInLawDeceased: false,
      FatherInLawDependentId: null,
      MotherInLawName: "",
      MotherInLawDeceased: false,
      MotherInLawDependentId: null,
      Sickness: [],
      SicknessOther: "",
      SicknessDetails: "",
      NewOld: "Active",
      Remark: "",
      DepartmentCode: "",
      LocationCode: "",
      Roster: "A",
      company: "",
      role: "U",
      SLocation: "",
      Status: "A",
      Qr: "N",
      DutyType: "",
      ExternalType: "",
      SubDutyTraditional: "",
      PayRollType: "",
      Designation: "",
      WorkLocation: "",
      Division: "",
      HomeTown: "",
      VehicleTypeOwned: "",
      TransportMethod2: [],
      BasisOfEmployment: "",
      AssignmentEndDate: "",
      WorkCategory: "",
      Grade: "",
      ApplicableShifts: "",
      LeavingDate: "",
      LeavingReason: "",
      AttendanceID: "",
    });
    setErrors({});
  };

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f6f9",
      }}
    >
      <HeaderBannerPage />

      <Box sx={{ padding: "24px", flex: 1 }}>
        {/* ── Top Banner ─────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "12px",
            background: "linear-gradient(135deg, #1e0153 0%, #154c79 100%)",
            color: "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            boxShadow: "0px 4px 12px rgba(30, 1, 83, 0.15)",
          }}
        >
          <Box>
            <Chip
              label="PERSONAL & CONFIDENTIAL"
              color="error"
              size="small"
              sx={{
                fontWeight: "bold",
                mb: 1,
                fontSize: "11px",
                letterSpacing: "0.5px",
                backgroundColor: "#ef4444",
              }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 0.5, fontFamily: "'Poppins', sans-serif" }}
            >
              Staff Information Database
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, fontSize: "13px" }}>
              Sri Dalada Maligawa
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* ════════════════════════════════════════════════
              LEFT: Multi-tab form
          ════════════════════════════════════════════════ */}
          <Grid item xs={12} lg={8}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
                overflow: "hidden",
                backgroundColor: "#ffffff",
              }}
            >
              {/* Tab Navigation */}
              <Tabs
                value={tabValue}
                onChange={(e, val) => setTabValue(val)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: "1px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                  "& .MuiTabs-indicator": { backgroundColor: "#1e0153" },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#64748b",
                    minHeight: "52px",
                    "&.Mui-selected": { color: "#1e0153" },
                  },
                }}
              >
                <Tab label="Service Info" icon={<BadgeIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                <Tab label="Residence" icon={<HomeIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                <Tab label="Personal & Education" icon={<SchoolIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                <Tab label="Vehicle & Commute" icon={<CarIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                <Tab label="Family" icon={<FamilyIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                <Tab label="Medical & History" icon={<MedicalIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                <Tab label="Employment" icon={<BadgeIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                <Tab label="Monthly Payment" icon={<BadgeIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                <Tab label="Welfare Benefits" icon={<FamilyIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
              </Tabs>

              {/* ══════════════════════════════════════════
                  TAB 0 — SERVICE INFO
              ══════════════════════════════════════════ */}
              {tabValue === 0 && (
                <Box sx={{ p: 3 }}>

                  {/* ── Employee Photo ─────────────────────── */}
                  <Box sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: "10px",
                    border: "1px dashed #c7d2fe",
                    backgroundColor: "#f5f3ff",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    flexWrap: "wrap",
                  }}>
                    {/* Preview box */}
                    <Box
                      onClick={() => document.getElementById("emp-photo-input-form").click()}
                      sx={{
                        width: 100, height: 120,
                        borderRadius: "8px",
                        border: "2px solid #a5b4fc",
                        overflow: "hidden",
                        backgroundColor: "#e0e7ff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer",
                        flexShrink: 0,
                        position: "relative",
                        "&:hover .img-overlay": { opacity: 1 },
                      }}
                    >
                      {(imagePreviewUrl || (savedImageUrl && !failedImageIds.has(formData.ServiceNo))) ? (
                        <img
                          src={imagePreviewUrl || savedImageUrl}
                          alt="Employee"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            // Only retry the saved (server) image, not a local blob preview
                            if (!imagePreviewUrl && savedImageUrl) {
                              retryEmployeeImage(formData.ServiceNo);
                            }
                          }}
                          onLoad={(e) => {
                            e.target.style.display = "block";
                            justUploadedImageRef.current = false;
                          }}
                        />
                      ) : (
                        <PersonIcon sx={{ fontSize: 52, color: "#a5b4fc" }} />
                      )}
                      <Box className="img-overlay" sx={{
                        position: "absolute", inset: 0,
                        backgroundColor: "rgba(30,1,83,0.45)",
                        display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", gap: 0.5,
                        opacity: 0, transition: "opacity 0.2s",
                      }}>
                        <CameraAltIcon sx={{ color: "#fff", fontSize: 26 }} />
                        <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: 600 }}>
                          Change
                        </Typography>
                      </Box>
                    </Box>

                    <input
                      id="emp-photo-input-form"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleImageFileChange}
                    />

                    {/* Text + action */}
                    <Box sx={{ flex: 1, minWidth: 160 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e0153", mb: 0.5, fontSize: "13px" }}>
                        Employee Photo
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 1.5, fontSize: "12px" }}>
                        {imageFile
                          ? `Selected: ${imageFile.name}`
                          : (savedImageUrl ? "Photo loaded. Click to change." : "No photo yet. Click the box or button to add.")}
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CameraAltIcon sx={{ fontSize: 15 }} />}
                          onClick={() => document.getElementById("emp-photo-input-form").click()}
                          sx={{
                            fontSize: "12px", fontWeight: 600, textTransform: "none",
                            borderColor: "#1e0153", color: "#1e0153", borderRadius: "6px",
                            "&:hover": { backgroundColor: "#f5f3ff" },
                          }}
                        >
                          Select Photo
                        </Button>

                        {imageFile && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={imageUploading
                              ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                              : <PhotoCameraIcon sx={{ fontSize: 15 }} />}
                            disabled={imageUploading}
                            onClick={handleImageUpload}
                            sx={{
                              fontSize: "12px", fontWeight: 600, textTransform: "none",
                              backgroundColor: "#1e0153", borderRadius: "6px",
                              "&:hover": { backgroundColor: "#15003e" },
                              "&.Mui-disabled": { backgroundColor: "#a5b4fc", color: "#fff" },
                            }}
                          >
                            {imageUploading ? "Uploading..." : "Upload Photo"}
                          </Button>
                        )}

                        {imageFile && !imageUploading && (
                          <Button
                            size="small"
                            onClick={() => { setImageFile(null); setImagePreviewUrl(""); setImageUploadError(""); }}
                            sx={{ fontSize: "12px", color: "#ef4444", textTransform: "none", minWidth: 0 }}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>

                      {imageUploadError && (
                        <Typography variant="caption" sx={{ color: "#ef4444", display: "block", mt: 0.5, fontSize: "11px" }}>
                          {imageUploadError}
                        </Typography>
                      )}
                    </Box>

                    {/* Employee status toggle */}
                    <Box sx={{ ml: { sm: "auto" }, minWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e0153", mb: 1, fontSize: "13px" }}>
                        Employee Status
                      </Typography>
                      <Box sx={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #c7d2fe", backgroundColor: "#fff" }}>
                        {["Active", "Inactive"].map((opt) => {
                          const selected = (formData.NewOld || "Active") === opt;
                          const color = opt === "Active" ? "#15803d" : "#dc2626";
                          return (
                            <Button
                              key={opt}
                              size="small"
                              disableElevation
                              onClick={() => {
                                handleFormChange({ target: { name: "NewOld", value: opt } });
                                // switching back to Active clears the exit details
                                if (opt === "Active") {
                                  handleFormChange({ target: { name: "LeavingDate", value: "" } });
                                  handleFormChange({ target: { name: "LeavingReason", value: "" } });
                                }
                              }}
                              sx={{
                                flex: 1, borderRadius: 0, fontSize: "12px", fontWeight: 700, textTransform: "none",
                                backgroundColor: selected ? color : "transparent",
                                color: selected ? "#fff" : "#64748b",
                                "&:hover": { backgroundColor: selected ? color : "#f5f3ff" },
                              }}
                            >
                              {opt}
                            </Button>
                          );
                        })}
                      </Box>
                      {formData.NewOld === "Inactive" && (
                        <Typography variant="caption" sx={{ color: "#dc2626", display: "block", mt: 0.75, fontSize: "11px" }}>
                          Enter the Leaving Date and Reason in the Employment tab.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <SectionTitle>Service Details</SectionTitle>
                  <Grid container spacing={2}>
                                        <Grid item xs={12} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          name="ServiceNoType"
                          value={serviceNoType}
                          onChange={handleServiceNoTypeChange}
                          label="Type"
                        >
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="T">T-Type</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Service Number"
                        name="ServiceNo"
                        inputMode={serviceNoType === "T" ? "text" : "numeric"}
                        value={formData.ServiceNo}
                        onChange={handleFormChange}
                        error={!!errors.ServiceNo}
                        helperText={errors.ServiceNo}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BadgeIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Attendance ID"
                        name="AttendanceID"
                        inputMode="numeric"
                        value={formData.AttendanceID}
                        onChange={handleFormChange}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BadgeIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name with Initials (English)"
                        name="ReportName"
                        value={formData.ReportName}
                        onChange={handleFormChange}
                        error={!!errors.ReportName}
                        helperText={errors.ReportName}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name (Sinhala)"
                        name="FullName"
                        value={formData.FullName}
                        onChange={handleFormChange}
                        error={!!errors.FullName}
                        helperText={errors.FullName}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <TranslateIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Calling Name"
                        name="FirstName"
                        value={formData.FirstName}
                        onChange={handleFormChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Duty Type</InputLabel>
                        <Select name="DutyType" value={formData.DutyType || ""} onChange={handleFormChange} label="Duty Type">
                          <MenuItem value="Operational">Operational</MenuItem>
                          <MenuItem value="Traditional">Traditional</MenuItem>
                          <MenuItem value="Traditional – Thewa">Traditional – Thewa</MenuItem>
                          <MenuItem value="Temples">Temples</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                          <MenuItem value="External">External</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {formData.DutyType === "External" && (
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>External Type</InputLabel>
                          <Select name="ExternalType" value={formData.ExternalType || ""} onChange={handleFormChange} label="External Type">
                            <MenuItem value="Army">Army</MenuItem>
                            <MenuItem value="Navy">Navy</MenuItem>
                            <MenuItem value="Air Force">Air Force</MenuItem>
                            <MenuItem value="Police">Police</MenuItem>
                            <MenuItem value="Others">Others</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sub Duty Traditional</InputLabel>
                        <Select name="SubDutyTraditional" value={formData.SubDutyTraditional || ""} onChange={handleFormChange} label="Sub Duty Traditional">
                          {subDutyTraditionalOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>PayRoll Type</InputLabel>
                        <Select name="PayRollType" value={formData.PayRollType || ""} onChange={handleFormChange} label="PayRoll Type">
                          {payRollTypeOptions.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={designationOptions}
                        getOptionLabel={(opt) => opt.name || ""}
                        isOptionEqualToValue={(opt, val) => opt.code === val.code}
                        value={designationOptions.find((opt) => opt.code === formData.Designation) || null}
                        onChange={(e, newValue) =>
                          handleFormChange({ target: { name: "Designation", value: newValue ? newValue.code : "" } })
                        }
                        renderInput={(params) => <TextField {...params} label="Designation" size="small" fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={locationOptions}
                        getOptionLabel={(opt) => opt.name || ""}
                        isOptionEqualToValue={(opt, val) => opt.name === val.name}
                        value={locationOptions.find((opt) => opt.name === formData.WorkLocation) || null}
                        onChange={(e, newValue) => {
                          handleFormChange({ target: { name: "WorkLocation", value: newValue ? newValue.name : "" } });
                          handleFormChange({ target: { name: "LocationCode", value: newValue ? newValue.code : "" } });
                        }}
                        renderInput={(params) => <TextField {...params} label="Work Location" size="small" fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={divisionOptions}
                        getOptionLabel={(opt) => opt.name || ""}
                        isOptionEqualToValue={(opt, val) => opt.code === val.code}
                        value={divisionOptions.find((opt) => opt.code === formData.Division) || null}
                        onChange={(e, newValue) =>
                          handleFormChange({ target: { name: "Division", value: newValue ? newValue.code : "" } })
                        }
                        renderInput={(params) => <TextField {...params} label="Division" size="small" fullWidth />}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ══════════════════════════════════════════
                  TAB 1 — RESIDENCE
              ══════════════════════════════════════════ */}
              {tabValue === 1 && (
                <Box sx={{ p: 3 }}>
                  <SectionTitle>Permanent Residence Details</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={2} label="Address" name="HomeAddress" value={formData.HomeAddress} onChange={handleFormChange} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label="Distance from Maligawa (KM)" name="HomeDistance" value={formData.HomeDistance} onChange={handleFormChange} size="small" inputProps={{ min: 0, step: 0.1 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Mobile Number" name="MobileNo" value={formData.MobileNo} onChange={handleFormChange}
                        error={!!errors.MobileNo} helperText={errors.MobileNo} size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Land Phone - Home" name="HomeTelephone" value={formData.HomeTelephone} onChange={handleFormChange} size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Grama Niladari Division" name="GramaSevakaArea" value={formData.GramaSevakaArea} onChange={handleFormChange} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Divisional Secretariat" name="DSDivision" value={formData.DSDivision} onChange={handleFormChange} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Police Station" name="PoliceArea" value={formData.PoliceArea} onChange={handleFormChange} size="small" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Electoral Division" name="ElectoralDivision" value={formData.ElectoralDivision} onChange={handleFormChange} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Home Town" name="HomeTown" value={formData.HomeTown || ""} onChange={handleFormChange} size="small" />
                    </Grid>
                  </Grid>
                  <SectionDivider />
                  <SectionTitle>Temporary Residence Details (Optional)</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={2} label="Address" name="TemporaryAddress" value={formData.TemporaryAddress} onChange={handleFormChange} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label="Distance from Maligawa (KM)" name="TemporaryDistance" value={formData.TemporaryDistance} onChange={handleFormChange} size="small" inputProps={{ min: 0, step: 0.1 }} />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ══════════════════════════════════════════
                  TAB 2 — PERSONAL & EDUCATION
              ══════════════════════════════════════════ */}
              {tabValue === 2 && (
                <Box sx={{ p: 3 }}>
                  <SectionTitle>Educational & Professional Qualifications</SectionTitle>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>Highest Educational Qualification</Typography>
                    <FormControl size="small" sx={{ minWidth: 300 }}>
                      <InputLabel>Select Qualification</InputLabel>
                      <Select name="HighestEducation" value={formData.HighestEducation} onChange={handleFormChange} label="Select Qualification">
                        {educationOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                      </Select>
                    </FormControl>
                    {formData.HighestEducation === "Other" && (
                      <TextField fullWidth label='Please specify "Other" qualification' name="HighestEducationOther" value={formData.HighestEducationOther} onChange={handleFormChange} size="small" sx={{ mt: 1.5 }} />
                    )}
                  </Box>
                  <Box sx={{ mt: 2.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>Other Qualifications</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                      {otherQualificationOptions.map((opt) => (
                        <FormControlLabel key={opt} control={<Checkbox size="small" checked={formData.OtherQualifications.includes(opt)} onChange={() => handleCheckboxToggle("OtherQualifications", opt)} sx={{ color: "#1e0153", "&.Mui-checked": { color: "#1e0153" } }} />} label={<Typography variant="body2" sx={{ fontSize: "13px" }}>{opt}</Typography>} />
                      ))}
                    </Box>
                    {formData.OtherQualifications.includes("Other") && (
                      <TextField fullWidth label='Please specify "Other" qualification' name="OtherQualificationsOther" value={formData.OtherQualificationsOther} onChange={handleFormChange} size="small" sx={{ mt: 1.5 }} />
                    )}
                  </Box>
                  <Box sx={{ mt: 2.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>Other Skills</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                      {otherSkillOptions.map((opt) => (
                        <FormControlLabel key={opt} control={<Checkbox size="small" checked={formData.OtherSkills.includes(opt)} onChange={() => handleCheckboxToggle("OtherSkills", opt)} sx={{ color: "#1e0153", "&.Mui-checked": { color: "#1e0153" } }} />} label={<Typography variant="body2" sx={{ fontSize: "13px" }}>{opt}</Typography>} />
                      ))}
                    </Box>
                    {formData.OtherSkills.includes("Other") && (
                      <TextField fullWidth label='Please specify "Other" skill' name="OtherSkillsOther" value={formData.OtherSkillsOther} onChange={handleFormChange} size="small" sx={{ mt: 1.5 }} />
                    )}
                  </Box>
                  <SectionDivider />
                  <SectionTitle>Personal Information</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DatePickerField label="Date of Birth" name="BirthDate" value={formData.BirthDate} onChange={handleFormChange} error={errors.BirthDate} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="NIC Number" name="NIDNo" value={formData.NIDNo} onChange={handleFormChange} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Marital Status</InputLabel>
                        <Select name="MaritalStatus" value={formData.MaritalStatus} onChange={handleFormChange} label="Marital Status">
                          <MenuItem value="Single">Single</MenuItem>
                          <MenuItem value="Married">Married</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Blood Group</InputLabel>
                        <Select name="BloodGroup" value={formData.BloodGroup} onChange={handleFormChange} label="Blood Group">
                          {bloodGroupOptions.map((bg) => <MenuItem key={bg} value={bg}>{bg}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ══════════════════════════════════════════
                  TAB 3 — VEHICLE & COMMUTE
              ══════════════════════════════════════════ */}
              {tabValue === 3 && (
                <Box sx={{ p: 3 }}>
                  <SectionTitle>Vehicle & Commute Details</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Driving License Number" name="LicenceNo" value={formData.LicenceNo} onChange={handleFormChange} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Own House</InputLabel>
                        <Select name="OwnsHouse" value={formData.OwnsHouse} onChange={handleFormChange} label="Own House">
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Own Vehicle</InputLabel>
                        <Select name="OwnsVehicle" value={formData.OwnsVehicle} onChange={handleFormChange} label="Own Vehicle">
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {formData.OwnsVehicle === "Yes" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Type of Vehicle" name="VehicleType" value={formData.VehicleType} onChange={handleFormChange} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Vehicle Number" name="VehicleNumber" value={formData.VehicleNumber} onChange={handleFormChange} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Registered Owner of Vehicle" name="VehicleRegisterOwner" value={formData.VehicleRegisterOwner} onChange={handleFormChange} size="small" />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Transport Method (Primary)</InputLabel>
                        <Select name="TransportMethod" value={formData.TransportMethod} onChange={handleFormChange} label="Transport Method (Primary)">
                          {commuteOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    {formData.TransportMethod === "Other" && (
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label='Please specify "Other" transport method' name="TransportMethodOther" value={formData.TransportMethodOther} onChange={handleFormChange} size="small" />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Transport Method (Secondary)</InputLabel>
                        <Select
                          multiple
                          name="TransportMethod2"
                          value={Array.isArray(formData.TransportMethod2) ? formData.TransportMethod2 : (formData.TransportMethod2 ? [formData.TransportMethod2] : [])}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Cap selection at 2 options
                            const limited = value.length > 2 ? value.slice(-2) : value;
                            setFormData((prev) => ({ ...prev, TransportMethod2: limited }));
                          }}
                          label="Transport Method (Secondary)"
                          renderValue={(selected) => selected.join(", ")}
                        >
                          {commuteOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              <Checkbox
                                size="small"
                                checked={(formData.TransportMethod2 || []).includes(opt)}
                                sx={{ color: "#1e0153", "&.Mui-checked": { color: "#1e0153" } }}
                              />
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type of Vehicle (Used for Commute)</InputLabel>
                        <Select name="VehicleTypeOwned" value={formData.VehicleTypeOwned || ""} onChange={handleFormChange} label="Type of Vehicle (Used for Commute)">
                          <MenuItem value="Motor Vehicle">Motor Vehicle</MenuItem>
                          <MenuItem value="Motor Cycle">Motor Cycle</MenuItem>
                          <MenuItem value="3 Wheeler">3 Wheeler</MenuItem>
                          <MenuItem value="Push Bicycle">Push Bicycle</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Shirt Collar Size</InputLabel>
                        <Select name="CollarSize" value={formData.CollarSize || ""} onChange={handleFormChange} label="Shirt Collar Size">
                          {collarSizeOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    {formData.CollarSize === "Other" && (
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label='Please specify "Other" collar size' name="CollarSizeOther" value={formData.CollarSizeOther || ""} onChange={handleFormChange} size="small" />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* ══════════════════════════════════════════
                  TAB 4 — FAMILY
              ══════════════════════════════════════════ */}
              {tabValue === 4 && (
                <Box sx={{ p: 3 }}>
                  <SectionTitle>Emergency Contact Details</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Full Name" name="ECName" value={formData.ECName} onChange={handleFormChange} size="small" /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Phone Number" name="ECMobile" value={formData.ECMobile} onChange={handleFormChange} size="small" InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment> }} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Relationship" name="ECRelationship" value={formData.ECRelationship} onChange={handleFormChange} size="small" /></Grid>
                    <Grid item xs={12}><TextField fullWidth label="Address" name="ECAddress" value={formData.ECAddress} onChange={handleFormChange} size="small" /></Grid>
                  </Grid>
                  <SectionDivider />
                  {formData.MaritalStatus === "Married" && (
                    <>
                      <SectionTitle>Details of Wife (If Married)</SectionTitle>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Full Name" name="SpouseFullName" value={formData.SpouseFullName} onChange={handleFormChange} size="small" /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Employment (if employed)" name="SpouseOccupation" value={formData.SpouseOccupation} onChange={handleFormChange} size="small" /></Grid>
                        <Grid item xs={12} sm={6}><DatePickerField label="Date of Birth" name="SpouseBirthDate" value={formData.SpouseBirthDate} onChange={handleFormChange} /></Grid>
                      </Grid>
                      <SectionDivider />
                    </>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <SectionTitle>Details of Children</SectionTitle>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddChild} sx={{ textTransform: "none", borderColor: "#1e0153", color: "#1e0153", fontWeight: 600, borderRadius: "6px" }}>Add Child</Button>
                  </Box>
                  {children.length === 0 && <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2, fontSize: "13px" }}>No children added yet. Click "Add Child" to add.</Typography>}
                  {children.map((child, index) => (
                    <Box key={child.id} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", p: 2, mb: 2, backgroundColor: "#f8fafc" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e0153" }}>Child {index + 1}</Typography>
                        <IconButton size="small" onClick={() => handleRemoveChild(child.id)} sx={{ color: "#ef4444" }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Name" value={child.Name} size="small" onChange={(e) => handleChildChange(child.id, "Name", e.target.value)} /></Grid>
                        <Grid item xs={12} sm={6}><DatePickerField label="Date of Birth" name="BirthDate" value={child.BirthDate} onChange={(e) => handleChildChange(child.id, "BirthDate", e.target.value)} /></Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small"><InputLabel>Sex</InputLabel>
                            <Select value={child.Sex} label="Sex" onChange={(e) => handleChildChange(child.id, "Sex", e.target.value)}>
                              <MenuItem value="Male">Male</MenuItem><MenuItem value="Female">Female</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="School / University / Employer" value={child.School} size="small" onChange={(e) => handleChildChange(child.id, "School", e.target.value)} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Present Grade or Other Details" value={child.Grade} size="small" onChange={(e) => handleChildChange(child.id, "Grade", e.target.value)} /></Grid>
                      </Grid>
                    </Box>
                  ))}
                  <SectionDivider />
                  <SectionTitle>Details of Parents</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}><TextField fullWidth label="Father's Name" name="FatherName" value={formData.FatherName} onChange={handleFormChange} size="small" /></Grid>
                    <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}><FormControlLabel control={<Checkbox checked={formData.FatherDeceased} onChange={(e) => setFormData((prev) => ({ ...prev, FatherDeceased: e.target.checked }))} sx={{ color: "#1e0153", "&.Mui-checked": { color: "#1e0153" } }} />} label={<Typography variant="body2">Deceased</Typography>} /></Grid>
                    <Grid item xs={12} sm={8}><TextField fullWidth label="Mother's Name" name="MotherName" value={formData.MotherName} onChange={handleFormChange} size="small" /></Grid>
                    <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}><FormControlLabel control={<Checkbox checked={formData.MotherDeceased} onChange={(e) => setFormData((prev) => ({ ...prev, MotherDeceased: e.target.checked }))} sx={{ color: "#1e0153", "&.Mui-checked": { color: "#1e0153" } }} />} label={<Typography variant="body2">Deceased</Typography>} /></Grid>
                    <Grid item xs={12} sm={8}><TextField fullWidth label="Father-in-Law Name" name="FatherInLawName" value={formData.FatherInLawName} onChange={handleFormChange} size="small" /></Grid>
                    <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}><FormControlLabel control={<Checkbox checked={formData.FatherInLawDeceased} onChange={(e) => setFormData((prev) => ({ ...prev, FatherInLawDeceased: e.target.checked }))} sx={{ color: "#1e0153", "&.Mui-checked": { color: "#1e0153" } }} />} label={<Typography variant="body2">Deceased</Typography>} /></Grid>
                    <Grid item xs={12} sm={8}><TextField fullWidth label="Mother-in-Law Name" name="MotherInLawName" value={formData.MotherInLawName} onChange={handleFormChange} size="small" /></Grid>
                    <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}><FormControlLabel control={<Checkbox checked={formData.MotherInLawDeceased} onChange={(e) => setFormData((prev) => ({ ...prev, MotherInLawDeceased: e.target.checked }))} sx={{ color: "#1e0153", "&.Mui-checked": { color: "#1e0153" } }} />} label={<Typography variant="body2">Deceased</Typography>} /></Grid>
                  </Grid>
                </Box>
              )}

              {/* ══════════════════════════════════════════
                  TAB 5 — MEDICAL & HISTORY
              ══════════════════════════════════════════ */}
              {tabValue === 5 && (
                <Box sx={{ p: 3 }}>
                  <SectionTitle>Sickness of Employee</SectionTitle>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                      {illnessOptions.map((opt) => (
                        <FormControlLabel key={opt} control={<Checkbox size="small" checked={formData.Sickness.includes(opt)} onChange={() => handleCheckboxToggle("Sickness", opt)} sx={{ color: "#1e0153", "&.Mui-checked": { color: "#1e0153" } }} />} label={<Typography variant="body2" sx={{ fontSize: "13px" }}>{opt}</Typography>} />
                      ))}
                    </Box>
                    {formData.Sickness.includes("Other") && (
                      <TextField fullWidth label='Please specify "Other" sickness' name="SicknessOther" value={formData.SicknessOther} onChange={handleFormChange} size="small" sx={{ mt: 1.5 }} />
                    )}
                    {formData.Sickness.length > 0 && (
                      <TextField fullWidth multiline rows={2} label="Details" name="SicknessDetails" value={formData.SicknessDetails} onChange={handleFormChange} size="small" sx={{ mt: 1.5 }} />
                    )}
                  </Box>
                  <SectionDivider />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <SectionTitle>Disability / Long Term / Critical Illnesses of Family Members</SectionTitle>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddFamilyIllness} sx={{ textTransform: "none", borderColor: "#1e0153", color: "#1e0153", fontWeight: 600, borderRadius: "6px", whiteSpace: "nowrap" }}>Add Member</Button>
                  </Box>
                  {familyIllnesses.length === 0 && <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2, fontSize: "13px" }}>No family illness records added yet.</Typography>}
                  {familyIllnesses.map((fi, index) => (
                    <Box key={fi.id} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", p: 2, mb: 2, backgroundColor: "#f8fafc" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e0153" }}>Member {index + 1}</Typography>
                        <IconButton size="small" onClick={() => handleRemoveFamilyIllness(fi.id)} sx={{ color: "#ef4444" }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Name" value={fi.Name} size="small" onChange={(e) => handleFamilyIllnessChange(fi.id, "Name", e.target.value)} /></Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth size="small"><InputLabel>Relationship</InputLabel>
                            <Select value={fi.Relationship} label="Relationship" onChange={(e) => handleFamilyIllnessChange(fi.id, "Relationship", e.target.value)}>
                              {familyRelationshipOptions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Disability / Illness" value={fi.Illness} size="small" onChange={(e) => handleFamilyIllnessChange(fi.id, "Illness", e.target.value)} /></Grid>
                      </Grid>
                    </Box>
                  ))}
                  <SectionDivider />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <SectionTitle>Details of Previous Employment</SectionTitle>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddPrevJob} sx={{ textTransform: "none", borderColor: "#1e0153", color: "#1e0153", fontWeight: 600, borderRadius: "6px" }}>Add Employment</Button>
                  </Box>
                  {prevEmployments.length === 0 && <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2, fontSize: "13px" }}>No previous employment added yet.</Typography>}
                  {prevEmployments.map((job, index) => (
                    <Box key={job.id} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", p: 2, mb: 2, backgroundColor: "#f8fafc" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e0153" }}>Employment {index + 1}</Typography>
                        <IconButton size="small" onClick={() => handleRemovePrevJob(job.id)} sx={{ color: "#ef4444" }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Institute" value={job.Institute} size="small" onChange={(e) => handlePrevJobChange(job.id, "Institute", e.target.value)} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Position" value={job.Position} size="small" onChange={(e) => handlePrevJobChange(job.id, "Position", e.target.value)} /></Grid>
                        <Grid item xs={12} sm={6}><DatePickerField label="From" name="From" value={job.From} onChange={(e) => handlePrevJobChange(job.id, "From", e.target.value)} /></Grid>
                        <Grid item xs={12} sm={6}><DatePickerField label="To" name="To" value={job.To} onChange={(e) => handlePrevJobChange(job.id, "To", e.target.value)} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Reason for Leaving" value={job.LeavingReason} size="small" onChange={(e) => handlePrevJobChange(job.id, "LeavingReason", e.target.value)} /></Grid>
                      </Grid>
                    </Box>
                  ))}
                  <SectionDivider />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <SectionTitle>Any Relatives Working at Dalada Maligawa</SectionTitle>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddRelative} sx={{ textTransform: "none", borderColor: "#1e0153", color: "#1e0153", fontWeight: 600, borderRadius: "6px" }}>Add Relative</Button>
                  </Box>
                  {relatives.length === 0 && <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2, fontSize: "13px" }}>No relatives added yet.</Typography>}
                  {relatives.map((rel, index) => (
                    <Box key={rel.id} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", p: 2, mb: 2, backgroundColor: "#f8fafc" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e0153" }}>Relative {index + 1}</Typography>
                        <IconButton size="small" onClick={() => handleRemoveRelative(rel.id)} sx={{ color: "#ef4444" }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Name" value={rel.Name} size="small" onChange={(e) => handleRelativeChange(rel.id, "Name", e.target.value)} /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Service No" value={rel.ServiceNo} size="small" onChange={(e) => handleRelativeChange(rel.id, "ServiceNo", e.target.value)} /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Relationship" value={rel.Relationship} size="small" onChange={(e) => handleRelativeChange(rel.id, "Relationship", e.target.value)} /></Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              )}

              {/* ══════════════════════════════════════════
                  TAB 6 — EMPLOYMENT
              ══════════════════════════════════════════ */}
              {tabValue === 6 && (
                <Box sx={{ p: 3 }}>
                  <SectionTitle>Employment Details</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DatePickerField
                        label="Date of Recruitment"
                        name="RecruitmentDate"
                        value={formData.RecruitmentDate}
                        onChange={handleFormChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Basis of Employment</InputLabel>
                        <Select name="BasisOfEmployment" value={formData.BasisOfEmployment || ""} onChange={handleFormChange} label="Basis of Employment">
                          <MenuItem value="Regular">Regular</MenuItem>
                          <MenuItem value="Probation">Probation</MenuItem>
                          <MenuItem value="Assignment">Assignment</MenuItem>
                          <MenuItem value="Temporary">Temporary</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {formData.BasisOfEmployment === "Assignment" && (
                      <Grid item xs={12} sm={6}>
                        <DatePickerField
                          label="End Date (if on Assignment Basis)"
                          name="AssignmentEndDate"
                          value={formData.AssignmentEndDate || ""}
                          onChange={handleFormChange}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={workCategoryOptions}
                        getOptionLabel={(opt) => opt.name || ""}
                        isOptionEqualToValue={(opt, val) => opt.code === val.code}
                        value={workCategoryOptions.find((opt) => opt.code === formData.WorkCategory) || null}
                        onChange={(e, newValue) =>
                          handleFormChange({ target: { name: "WorkCategory", value: newValue ? newValue.code : "" } })
                        }
                        noOptionsText="No categories (add them in Lookup Management)"
                        renderInput={(params) => <TextField {...params} label="Work Category" size="small" fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Grade"
                        name="Grade"
                        value={formData.Grade || ""}
                        onChange={handleFormChange}
                        size="small"
                        helperText="LOV to be submitted"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Applicable Shift/s"
                        name="ApplicableShifts"
                        value={formData.ApplicableShifts || ""}
                        onChange={handleFormChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePickerField
                        label="Leaving Date"
                        name="LeavingDate"
                        value={formData.LeavingDate || ""}
                        onChange={handleFormChange}
                        required={formData.NewOld === "Inactive"}
                        error={!!errors.LeavingDate}
                        helperText={errors.LeavingDate}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Leaving Reason"
                        name="LeavingReason"
                        value={formData.LeavingReason || ""}
                        onChange={handleFormChange}
                        size="small"
                        required={formData.NewOld === "Inactive"}
                        error={!!errors.LeavingReason}
                        helperText={errors.LeavingReason}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ══════════════════════════════════════════
                  TAB 7 — MONTHLY PAYMENT
              ══════════════════════════════════════════ */}
              {tabValue === 7 && (
                <Box sx={{ p: 3 }}>
                  <SectionTitle>Monthly Payment Details</SectionTitle>
                  <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setMonthlyPayments(prev => [...prev, { id: Date.now(), Year: new Date().getFullYear().toString(), Allowance: "", AdditionalAllowance: "", Total: "" }])}
                      sx={{ textTransform: "none", borderColor: "#1e0153", color: "#1e0153", fontWeight: 600, borderRadius: "6px" }}
                    >
                      Add Year
                    </Button>
                  </Box>
                  {monthlyPayments.length === 0 && (
                    <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2, fontSize: "13px" }}>No payment records added yet. Click "Add Year" to add.</Typography>
                  )}
                  <Box sx={{ overflowX: "auto" }}>
                    {monthlyPayments.length > 0 && (
                      <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                            <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "13px" }}>Year</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "13px" }}>Allowance</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "13px" }}>Additional Allowance</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "13px" }}>Total</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "13px" }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthlyPayments.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>
                                <TextField size="small" value={row.Year} onChange={(e) => setMonthlyPayments(prev => prev.map(r => r.id === row.id ? { ...r, Year: e.target.value, fromServer: false } : r))} sx={{ width: 90 }} />
                              </TableCell>
                              <TableCell>
                                <TextField size="small" value={row.Allowance} onChange={(e) => setMonthlyPayments(prev => prev.map(r => r.id === row.id ? { ...r, Allowance: e.target.value, fromServer: false, Total: (parseFloat(e.target.value || 0) + parseFloat(r.AdditionalAllowance || 0)).toString() } : r))} sx={{ width: 120 }} />
                              </TableCell>
                              <TableCell>
                                <TextField size="small" value={row.AdditionalAllowance} onChange={(e) => setMonthlyPayments(prev => prev.map(r => r.id === row.id ? { ...r, AdditionalAllowance: e.target.value, fromServer: false, Total: (parseFloat(r.Allowance || 0) + parseFloat(e.target.value || 0)).toString() } : r))} sx={{ width: 150 }} />
                              </TableCell>
                              <TableCell>
                                <TextField size="small" value={row.Total} InputProps={{ readOnly: true }} sx={{ width: 120, "& .MuiInputBase-input": { backgroundColor: "#f1f5f9" } }} />
                              </TableCell>
                              <TableCell>
                                <IconButton size="small" onClick={() => setMonthlyPayments(prev => prev.filter(r => r.id !== row.id))} sx={{ color: "#ef4444" }}><DeleteIcon fontSize="small" /></IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                </Box>
              )}

              {/* ══════════════════════════════════════════
                  TAB 8 — WELFARE BENEFITS / IMPORTANT INFO
              ══════════════════════════════════════════ */}
              {tabValue === 8 && (
                <Box sx={{ p: 3 }}>
                  <SectionTitle>Welfare Benefits / Financial Assistance Granted</SectionTitle>
                  <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setWelfareRecords(prev => [...prev, { id: Date.now(), Date: "", Category: "", Description: "", ValueOfGrant: "" }])}
                      sx={{ textTransform: "none", borderColor: "#1e0153", color: "#1e0153", fontWeight: 600, borderRadius: "6px" }}
                    >
                      Add Record
                    </Button>
                  </Box>
                  {welfareRecords.length === 0 && (
                    <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2, fontSize: "13px" }}>No welfare records added yet.</Typography>
                  )}
                  {welfareRecords.map((rec, index) => (
                    <Box key={rec.id} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", p: 2, mb: 2, backgroundColor: "#f8fafc" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e0153" }}>Record {index + 1}</Typography>
                        <IconButton size="small" onClick={() => setWelfareRecords(prev => prev.filter(r => r.id !== rec.id))} sx={{ color: "#ef4444" }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <DatePickerField label="Date" name="WelfareDate" value={rec.Date} onChange={(e) => setWelfareRecords(prev => prev.map(r => r.id === rec.id ? { ...r, Date: e.target.value } : r))} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select value={rec.Category} label="Category" onChange={(e) => setWelfareRecords(prev => prev.map(r => r.id === rec.id ? { ...r, Category: e.target.value } : r))}>
                              <MenuItem value="Medical">Medical</MenuItem>
                              <MenuItem value="Housing">Housing</MenuItem>
                              <MenuItem value="Educational">Educational</MenuItem>
                              <MenuItem value="Funeral">Funeral</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="Description" value={rec.Description} size="small" onChange={(e) => setWelfareRecords(prev => prev.map(r => r.id === rec.id ? { ...r, Description: e.target.value } : r))} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="Value of Grant" value={rec.ValueOfGrant} size="small" onChange={(e) => setWelfareRecords(prev => prev.map(r => r.id === rec.id ? { ...r, ValueOfGrant: e.target.value } : r))} />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  <SectionDivider />
                  <SectionTitle>Other Important Information</SectionTitle>
                  <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setImportantInfoRecords(prev => [...prev, { id: Date.now(), Date: "", Description: "", Remarks: "" }])}
                      sx={{ textTransform: "none", borderColor: "#1e0153", color: "#1e0153", fontWeight: 600, borderRadius: "6px" }}
                    >
                      Add Info
                    </Button>
                  </Box>
                  {importantInfoRecords.length === 0 && (
                    <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2, fontSize: "13px" }}>No important info records added yet.</Typography>
                  )}
                  {importantInfoRecords.map((info, index) => (
                    <Box key={info.id} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", p: 2, mb: 2, backgroundColor: "#f8fafc" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e0153" }}>Info {index + 1}</Typography>
                        <IconButton size="small" onClick={() => setImportantInfoRecords(prev => prev.filter(r => r.id !== info.id))} sx={{ color: "#ef4444" }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <DatePickerField label="Date" name="InfoDate" value={info.Date} onChange={(e) => setImportantInfoRecords(prev => prev.map(r => r.id === info.id ? { ...r, Date: e.target.value } : r))} />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField fullWidth label="Description" value={info.Description} size="small" onChange={(e) => setImportantInfoRecords(prev => prev.map(r => r.id === info.id ? { ...r, Description: e.target.value } : r))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Remarks" value={info.Remarks} size="small" onChange={(e) => setImportantInfoRecords(prev => prev.map(r => r.id === info.id ? { ...r, Remarks: e.target.value } : r))} />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              )}

              {/* ── Action Buttons ──────────────────────── */}
              <Box
                sx={{
                  p: 3,
                  backgroundColor: "#f8fafc",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => handleClearForm()}
                  sx={{ fontWeight: 600, textTransform: "none", borderColor: "#94a3b8", color: "#64748b", borderRadius: "8px", px: 3, py: 1 }}
                >
                  Clear Form
                </Button>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddEmployeeToQueue}
                    sx={{ fontWeight: 600, textTransform: "none", borderColor: "#1e0153", color: "#1e0153", borderRadius: "8px", px: 3, py: 1 }}
                  >
                    {editingId ? "Update" : "Add to Queue"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SubmitIcon />}
                    onClick={() => setShowDirectDialog(true)}
                    sx={{ fontWeight: 600, textTransform: "none", backgroundColor: "#22c55e", color: "#ffffff", borderRadius: "8px", px: 3, py: 1, boxShadow: "0px 2px 6px rgba(34,197,94,0.15)", "&:hover": { backgroundColor: "#16a34a" } }}
                  >
                    Register & Save Directly
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* ════════════════════════════════════════════════
              RIGHT: Session employees panel
          ════════════════════════════════════════════════ */}
          <Grid item xs={12} lg={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
                backgroundColor: "#ffffff",
                minHeight: "450px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Panel header */}
              <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e0153" }}>
                  Employees ({filteredEmployees.length})
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {sessionEmployees.some((emp) => emp.statusType === "Queued") && (
                    <Button
                      variant="contained"
                      onClick={() => setShowSubmitDialog(true)}
                      sx={{ fontSize: "12px", fontWeight: 600, textTransform: "none", backgroundColor: "#1e0153", color: "#ffffff", borderRadius: "6px", "&:hover": { backgroundColor: "#15003e" } }}
                    >
                      Submit Batch ({sessionEmployees.filter((e) => e.statusType === "Queued").length})
                    </Button>
                  )}
                  {/* Export Excel button */}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleExportExcel}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "none",
                        borderColor: "#1e0153",
                        color: "#1e0153",
                        borderRadius: "6px",
                        "&:hover": { backgroundColor: "#f5f3ff" },
                      }}
                    >
                      Export Excel
                    </Button>
                  <Tooltip title="Refresh employee list from server">
                    <IconButton size="small" onClick={handleRefreshEmployees} sx={{ color: "#1e0153" }}>
                      {empLoading ? <CircularProgress size={18} sx={{ color: "#1e0153" }} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Search + Type filter */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name or service no..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={serviceNoFilter}
                    label="Type"
                    onChange={(e) => setServiceNoFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="T">T-Type</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Employee Status</InputLabel>
                  <Select
                    value={activeFilter}
                    label="Employee Status"
                    onChange={(e) => setActiveFilter(e.target.value)}
                  >
                    <MenuItem value="all">Both</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Error state */}
              {empError && (
                <Alert severity="warning" sx={{ borderRadius: "8px", mb: 2, fontSize: "12px" }}>
                  Could not load employees from server. Showing session data only.
                </Alert>
              )}

              {/* Loading state */}
              {empLoading && filteredEmployees.length === 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                  <CircularProgress size={32} sx={{ color: "#1e0153" }} />
                </Box>
              )}

              {/* Empty state */}
              {!empLoading && filteredEmployees.length === 0 && (
                <Alert severity="info" sx={{ borderRadius: "8px" }}>
                  {searchTerm ? "No employees match your search." : "No employees found."}
                </Alert>
              )}

              {/* Employee table */}
              {filteredEmployees.length > 0 && (
                <TableContainer sx={{ flex: 1, maxHeight: "520px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "12px", backgroundColor: "#f8fafc", width: 44 }}></TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "12px", backgroundColor: "#f8fafc" }}>Service No</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "12px", backgroundColor: "#f8fafc" }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "12px", backgroundColor: "#f8fafc" }} align="center">Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "12px", backgroundColor: "#f8fafc" }} align="center">Active</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#1e0153", fontSize: "12px", backgroundColor: "#f8fafc" }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEmployees.map((employee) => (
                        <TableRow key={employee.id} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                          <TableCell sx={{ py: 1, px: 1 }}>
                            <Box
                              sx={{
                                width: 32, height: 38,
                                borderRadius: "4px",
                                overflow: "hidden",
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#f1f5f9",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}
                            >
                              {employee.ServiceNo && !failedImageIds.has(employee.ServiceNo) ? (
                                <img
                                  src={getEmployeeImageUrl(employee.ServiceNo)}
                                  alt=""
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  onError={() => markImageFailed(employee.ServiceNo)}
                                />
                              ) : (
                                <PersonIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", py: 1.5 }}>{employee.ServiceNo}</TableCell>
                          <TableCell sx={{ fontSize: "12px", py: 1.5 }}>{employee.ReportName}</TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            <Chip
                              label={employee.statusType === "Saved" ? "Registered" : "Queued"}
                              size="small"
                              sx={{
                                fontSize: "10px",
                                fontWeight: "bold",
                                backgroundColor: employee.statusType === "Saved" ? "#dcfce7" : "#fef3c7",
                                color: employee.statusType === "Saved" ? "#15803d" : "#b45309",
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            <Chip
                              label={getEmployeeActiveStatus(employee)}
                              size="small"
                              sx={{
                                fontSize: "10px",
                                fontWeight: "bold",
                                backgroundColor: getEmployeeActiveStatus(employee) === "Inactive" ? "#fee2e2" : "#dcfce7",
                                color: getEmployeeActiveStatus(employee) === "Inactive" ? "#b91c1c" : "#15803d",
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditEmployee(employee)}
                              sx={{color: "#3b82f6"}}
                              >
                                <EditIcon fontSize="small"/>
                              </IconButton>
                              {/* <IconButton
                              size="small"
                              onClick={() => handleDeleteEmployee(employee.id)}
                              sx={{color: "#ef4444", ml: 0.5}}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton> */}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* ── Confirm Direct Register Dialog ──────────── */}
      <Dialog open={showDirectDialog} onClose={() => setShowDirectDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1e0153" }}>Confirm Registration</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, fontSize: "14px", color: "#64748b" }}>
            Are you sure you want to register this employee directly to the database?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setShowDirectDialog(false)} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDirectRegister} disabled={submitting}
            sx={{ backgroundColor: "#22c55e", color: "#ffffff", textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#16a34a" } }}>
            {submitting ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Batch Submit Dialog ─────────────── */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1e0153" }}>Confirm Batch Submit</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, fontSize: "14px", color: "#64748b" }}>
            {`You are about to register ${sessionEmployees.filter((e) => e.statusType === "Queued").length} queued employee(s). Proceed?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setShowSubmitDialog(false)} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitAll} disabled={submitting}
            sx={{ backgroundColor: "#1e0153", color: "#ffffff", textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#15003e" } }}>
            {submitting ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar Notifications ───────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: "8px", fontWeight: 500 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeRegister;